"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Checkbox } from "../../ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../ui/popover";
import { Search, Upload, Loader2, RefreshCw, Save, ChevronDown, Plus, X } from "lucide-react";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { DocumentManager } from "@/components/planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";

const NO_SBP_MESSAGE =
  "The entity does not have any share based payment transactions in accordance with Ind AS 102 – Share Based Payment.";

const ESOP_JSON_FILE = "Execution_EBE_ESOP.json";

/** Same persistence pattern as custom procedures (Execution_EBE_Responses.json + db.json section). */
const EBE_WORKSPACE_PREFIX = "EBE";
const IND_AS_102_QUESTION_ID = "EBE_SBP_IND_AS_102_ASSESSMENT";
const IND_AS_102_INFERENCE_QUESTION_ID = "EBE_SBP_INFERENCE";
const IND_AS_102_SECTION_KEY = "execution-substantive:Employee Benefits Expense";
const IND_AS_102_DOC_QUESTION =
  "Document the inference and conclusion from the above procedure performed.";

/** Full substantive UI snapshot saved with Ind AS 102 Save (v1). */
type SbpProcedureResponsesV1 = {
  q1?: string;
  q2?: string;
  q3?: string;
  q4?: string;
  q4b?: string;
  q5?: string;
  q6?: string;
  /** Shared by equity and cash ESOP working (single control in UI). */
  reportingDate?: string;
  equityAssessment?: {
    eq1?: string;
    eq1b?: string;
    eq1Document?: string;
    eq2?: string;
    eq2Document?: string;
  };
  cashAlternative?: {
    cashAltCounterparty?: string;
    cashAltFairValueEqual?: string;
    cashAltDocument?: string;
  };
  equityInputs?: {
    selectedFile?: string;
    referenceValue?: string;
    columnMappings?: Record<string, string>;
    exercisePeriodUnit?: string;
    exercisePeriodValue?: string;
    tableRowOverrides?: Record<string, { allotted_in_cy: string; allotted_in_py: string }>;
    unvestedLapsedCy?: string;
    selectedPartyNamesForLapse?: string[];
    reconUserRows?: Array<{ reason: string; amount: number }>;
    equitySelectedExpenseLedgers?: string[];
  };
  cashInputs?: {
    cashSelectedFile?: string;
    cashReferenceValue?: string;
    cashColumnMappings?: Record<string, string>;
    cashExercisePeriodUnit?: string;
    cashExercisePeriodValue?: string;
    cashTableRowOverrides?: Record<string, { allotted_in_cy: string; allotted_in_py: string }>;
    cashUnvestedLapsedCy?: string;
    cashSelectedPartyNamesForLapse?: string[];
    cashReconUserRows?: Array<{ reason: string; amount: number }>;
    cashSelectedExpenseLedgers?: string[];
  };
  sbpReserveUi?: {
    sbpReserveAmountAsPerFs?: number | null;
    sbpReserveReconRows?: Array<{ particulars: string; amount: number }>;
  };
  /** When ESOP JSON is not saved separately, these preserve the reconciliation figures. */
  equityReconciliationComputed?: {
    expenseAsPerPLSum?: number;
    expenseAsPerESOPWorking?: number;
    expenseBookedUnvestedLapsed?: number;
    netExpenseToPL?: number;
  };
  cashReconciliationComputed?: {
    cashExpenseAsPerPLSum?: number;
    cashExpenseAsPerESOPWorking?: number;
    cashExpenseBookedUnvestedLapsed?: number;
    cashNetExpenseToPL?: number;
  };
};

/** Parse DD-MM-YYYY to Date (midnight UTC) */
function parseDDMMYYYY(s: string): Date | null {
  if (!s || typeof s !== "string") return null;
  const parts = s.trim().split(/[-/]/);
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const year = parseInt(parts[2], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  const d = new Date(year, month, day);
  if (isNaN(d.getTime())) return null;
  return d;
}

/** Get financial year range (2 years) from reporting date: [startOfPY, endOfCY] */
function getTwoYearRangeFromReportingDate(reportingDateStr: string): { start: Date; end: Date } | null {
  const reportEnd = parseDDMMYYYY(reportingDateStr);
  if (!reportEnd) return null;
  const year = reportEnd.getFullYear();
  const month = reportEnd.getMonth();
  const day = reportEnd.getDate();
  // Assume reporting date is FY end (e.g. 31 March). CY = same calendar year as report date's FY end.
  const endOfCY = new Date(year, month, day);
  const startOfCY = new Date(year - 1, month, day + 1); // e.g. 1 Apr year-1
  const startOfPY = new Date(year - 2, month, day + 1); // e.g. 1 Apr year-2
  return { start: startOfPY, end: endOfCY };
}

/** Check if date is in range [start, end] (inclusive) */
function isDateInRange(d: Date | null, start: Date, end: Date): boolean {
  if (!d) return false;
  const t = d.getTime();
  return t >= start.getTime() && t <= end.getTime();
}

/** Check if date falls in the current year (FY of reporting date) - i.e. after start of CY */
function isInCurrentFY(d: Date | null, range: { start: Date; end: Date } | null): boolean {
  if (!d || !range) return false;
  const cyStart = new Date(range.end);
  cyStart.setFullYear(cyStart.getFullYear() - 1);
  cyStart.setDate(cyStart.getDate() + 1);
  return d.getTime() >= cyStart.getTime() && d.getTime() <= range.end.getTime();
}

/** Check if exercise_date is in CY (same FY as range.end) */
function isExerciseDateInCY(exerciseDateStr: string, range: { start: Date; end: Date } | null): boolean {
  const d = parseDDMMYYYY(exerciseDateStr);
  if (!d || !range) return false;
  const cyStart = new Date(range.end.getFullYear() - 1, range.end.getMonth(), range.end.getDate() + 1);
  return d.getTime() >= cyStart.getTime() && d.getTime() <= range.end.getTime();
}

/** Check if exercise_date is in next year (after range.end) */
function isExerciseDateInNextYear(exerciseDateStr: string, range: { start: Date; end: Date } | null): boolean {
  const d = parseDDMMYYYY(exerciseDateStr);
  if (!d || !range) return false;
  return d.getTime() > range.end.getTime();
}

export interface ESOPDataRow {
  employee_name: string;
  number_of_shares: number;
  grant_date: string;
  period_from: string;
  period_to: string;
  exercise_price: number;
  fair_value: number;
  exercise_date: string;
  expense_for_current_period: number;
  total_expense_till_date: number;
  allotted_in_cy: string;
  allotted_in_py: string;
  remarks: string;
}

interface ESOPEquitySbp {
  financial_year_start?: string;
  financial_year_end?: string;
  reporting_date?: string;
  recomputation?: {
    expense_as_per_esop_working_A?: number | string;
    expense_booked_till_date_on_unvested_lapsed_B?: number | string;
    net_expense_to_profit_and_loss_C?: number | string;
    expense_as_per_profit_and_loss_D?: number | string;
    difference_C_minus_D?: number | string;
    reconciliation_rows?: Array<{ reason?: string; particulars?: string; amount?: number | string }>;
    net_difference?: number | string;
  };
  data?: ESOPDataRow[];
}

/** SBP Reserve section in Execution_EBE_ESOP.json */
interface SbpReserveRecomputation {
  opening_balance?: number | null;
  add_expense?: number | null;
  less_vested_and_allotted?: number | null;
  less_vested_and_lapsed?: number | null;
  closing_sbpr?: number | null;
}

interface SbpReserve {
  amount_as_per_fs?: number | null;
  amount_as_per_recomputation?: SbpReserveRecomputation | null;
  difference?: number | string | null;
  reconciliation_rows?: Array<{ particulars: string; amount: number }>;
  net_difference?: number | string | null;
}

interface ESOPJson {
  equity_sbp?: ESOPEquitySbp;
  cash_sbp?: ESOPEquitySbp;
  sbp_reserve?: SbpReserve | null;
}

const NOTE_LINE_SBP_RESERVE = 30105;

const ESOP_SECTION = "Execution_EBE_ESOP";
const ESOP_CASH_SECTION = "Execution_EBE_ESOP";

const ESOP_FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "employee_party_name", label: "Employee/Party Name" },
  { key: "number_of_shares", label: "Number of Shares" },
  { key: "grant_date", label: "Grant Date" },
  { key: "period_from", label: "Period From" },
  { key: "period_to", label: "Period To" },
  { key: "exercise_price", label: "Exercise Price" },
  { key: "fair_value_sbp_reserve", label: "Fair Value for SBP Reserve" },
];

const CASH_ESOP_FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "employee_party_name", label: "Employee/Party Name" },
  { key: "number_of_shares", label: "Number of Shares" },
  { key: "grant_date", label: "Grant Date" },
  { key: "period_from", label: "Period From" },
  { key: "period_to", label: "Period To" },
  { key: "fair_value_sbp_reserve", label: "Fair Value for SBP Reserve" },
];

interface ExcelColumn {
  name: string;
  value: string;
}

interface ShareBasedPaymentProps {
  onBack?: () => void;
}

export default function ShareBasedPayment({ onBack }: ShareBasedPaymentProps) {
  const { isArchivalMode } = useArchivalMode();

  // Ind AS 102
  const [q1, setQ1] = useState<string>("");
  const [q2, setQ2] = useState<string>("");
  const [q3, setQ3] = useState<string>("");
  const [q4, setQ4] = useState<string>("");
  const [q4b, setQ4b] = useState<string>("");
  const [q5, setQ5] = useState<string>("");
  const [q6, setQ6] = useState<string>("");

  const [companyName, setCompanyName] = useState<string>("");
  const [actualPhase, setActualPhase] = useState<string>("Execution");
  const [ind102QuestionResponse, setInd102QuestionResponse] = useState<{
    textResponse?: { html: string; plainText: string } | null;
    documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
  }>({});
  const [ind102SaveStatus, setInd102SaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [ind102SaveError, setInd102SaveError] = useState<string | null>(null);
  const [ind102Saving, setInd102Saving] = useState(false);
  const [inferenceQuestionResponse, setInferenceQuestionResponse] = useState<{
    textResponse?: { html: string; plainText: string } | null;
    documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
  }>({});
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);
  const [inferenceSaving, setInferenceSaving] = useState(false);

  // Equity-Settled SBP assessment
  const [eq1, setEq1] = useState<string>("");
  const [eq1b, setEq1b] = useState<string>(""); // rebuttable presumption - only if eq1 Yes
  const [eq1Document, setEq1Document] = useState<string>(""); // if eq1b No
  const [eq2, setEq2] = useState<string>("");
  const [eq2Document, setEq2Document] = useState<string>("");

  // SBP with Cash Alternative (when Q6 is Yes)
  const [cashAltCounterparty, setCashAltCounterparty] = useState<string>(""); // "Employee" | "Other than Employee"
  const [cashAltFairValueEqual, setCashAltFairValueEqual] = useState<string>(""); // Yes/No when counterparty is Employee
  const [cashAltDocument, setCashAltDocument] = useState<string>(""); // when Employee + fair value equal Yes

  // ESOP Schedule upload & columns (same pattern as IPE Testing)
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});

  // Exercise period & Reporting date
  const [exercisePeriodUnit, setExercisePeriodUnit] = useState<string>("");
  const [exercisePeriodValue, setExercisePeriodValue] = useState<string>("");
  const [reportingDate, setReportingDate] = useState<string>("");

  // ESOP table: JSON from juggernaut Execution_EBE_ESOP.json
  const [esopJson, setEsopJson] = useState<ESOPJson | null>(null);
  const [esopLoadStatus, setEsopLoadStatus] = useState<"idle" | "loading" | "loaded" | "error">("idle");
  const [tableRowOverrides, setTableRowOverrides] = useState<Record<string, { allotted_in_cy: string; allotted_in_py: string }>>({});
  const [esopSaveStatus, setEsopSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // Unvested options lapsed in CY
  const [unvestedLapsedCy, setUnvestedLapsedCy] = useState<string>("");
  const [selectedPartyNamesForLapse, setSelectedPartyNamesForLapse] = useState<string[]>([]);

  // ESOP reconciliation summary – values from equity_sbp.recomputation in Execution_EBE_ESOP.json
  const [expenseAsPerPLSum, setExpenseAsPerPLSum] = useState<number>(0); // (D)
  const [expenseAsPerESOPWorking, setExpenseAsPerESOPWorking] = useState<number>(0); // (A)
  const [expenseBookedUnvestedLapsed, setExpenseBookedUnvestedLapsed] = useState<number>(0); // (B)
  const [netExpenseToPL, setNetExpenseToPL] = useState<number>(0); // (C)

  // SBP Reserve table (always visible at bottom) – data from Execution_EBE_ESOP.json sbp_reserve + note line 30105
  const [sbpReserveAmountAsPerFs, setSbpReserveAmountAsPerFs] = useState<number | null>(null); // from note line 30105 or JSON
  const [sbpReserveReconRows, setSbpReserveReconRows] = useState<Array<{ particulars: string; amount: number }>>([]);
  const [sbpReserveSaveStatus, setSbpReserveSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [sbpReserveLoadFsStatus, setSbpReserveLoadFsStatus] = useState<"idle" | "loading" | "loaded" | "error">("idle");
  const differenceCD = netExpenseToPL - expenseAsPerPLSum; // (C - D)
  const [reconUserRows, setReconUserRows] = useState<Array<{ reason: string; amount: number }>>([]);
  const reconUserRowsSum = reconUserRows.reduce((s, r) => s + (r.amount || 0), 0);
  const netDifference = differenceCD - reconUserRowsSum;

  // Ledger options for Expense as per Profit & Loss (D)
  const [equityExpenseLedgerOptions, setEquityExpenseLedgerOptions] = useState<Array<{ name: string; amount: number }>>([]);
  const [equitySelectedExpenseLedgers, setEquitySelectedExpenseLedgers] = useState<string[]>([]);

  const addReconUserRow = () => {
    setReconUserRows([...reconUserRows, { reason: "", amount: 0 }]);
  };
  const removeReconUserRow = (index: number) => {
    setReconUserRows(reconUserRows.filter((_, i) => i !== index));
  };
  const updateReconUserRow = (index: number, field: "reason" | "amount", value: string | number) => {
    const updated = [...reconUserRows];
    if (field === "reason") updated[index] = { ...updated[index], reason: value as string };
    else updated[index] = { ...updated[index], amount: typeof value === "number" ? value : parseFloat(String(value)) || 0 };
    setReconUserRows(updated);
  };

  // Cash-settled SBP (when Q5 is Yes): upload, table, unvested, recon
  const [cashFileSearchStatus, setCashFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [cashFoundFiles, setCashFoundFiles] = useState<string[]>([]);
  const [cashSelectedFile, setCashSelectedFile] = useState<string>("");
  const [cashReferenceValue, setCashReferenceValue] = useState<string>("");
  const [cashUploadStatus, setCashUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [cashExcelColumns, setCashExcelColumns] = useState<ExcelColumn[]>([]);
  const [cashIsLoadingExcelColumns, setCashIsLoadingExcelColumns] = useState(false);
  const [cashColumnMappings, setCashColumnMappings] = useState<Record<string, string>>({});
  const [cashExercisePeriodUnit, setCashExercisePeriodUnit] = useState<string>("");
  const [cashExercisePeriodValue, setCashExercisePeriodValue] = useState<string>("");
  const [cashTableRowOverrides, setCashTableRowOverrides] = useState<Record<string, { allotted_in_cy: string; allotted_in_py: string }>>({});
  const [cashEsopSaveStatus, setCashEsopSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [cashUnvestedLapsedCy, setCashUnvestedLapsedCy] = useState<string>("");
  const [cashSelectedPartyNamesForLapse, setCashSelectedPartyNamesForLapse] = useState<string[]>([]);
  const [cashExpenseAsPerPLSum, setCashExpenseAsPerPLSum] = useState<number>(0);
  const [cashExpenseAsPerESOPWorking, setCashExpenseAsPerESOPWorking] = useState<number>(0);
  const [cashExpenseBookedUnvestedLapsed, setCashExpenseBookedUnvestedLapsed] = useState<number>(0);
  const [cashNetExpenseToPL, setCashNetExpenseToPL] = useState<number>(0);
  const [cashReconUserRows, setCashReconUserRows] = useState<Array<{ reason: string; amount: number }>>([]);
  const cashReconUserRowsSum = cashReconUserRows.reduce((s, r) => s + (r.amount || 0), 0);
  const cashDifferenceCD = cashNetExpenseToPL - cashExpenseAsPerPLSum;
  const cashNetDifference = cashDifferenceCD - cashReconUserRowsSum;

  const [cashSelectedExpenseLedgers, setCashSelectedExpenseLedgers] = useState<string[]>([]);

  // Collapsible Step inputs (Upload + Mapping/Parameters/Run)
  const [equityStep1Open, setEquityStep1Open] = useState(false);
  const [equityStep2Open, setEquityStep2Open] = useState(false);
  const [cashStep1Open, setCashStep1Open] = useState(false);
  const [cashStep2Open, setCashStep2Open] = useState(false);

  const addCashReconUserRow = () => {
    setCashReconUserRows([...cashReconUserRows, { reason: "", amount: 0 }]);
  };
  const removeCashReconUserRow = (index: number) => {
    setCashReconUserRows(cashReconUserRows.filter((_, i) => i !== index));
  };
  const updateCashReconUserRow = (index: number, field: "reason" | "amount", value: string | number) => {
    const updated = [...cashReconUserRows];
    if (field === "reason") updated[index] = { ...updated[index], reason: value as string };
    else updated[index] = { ...updated[index], amount: typeof value === "number" ? value : parseFloat(String(value)) || 0 };
    setCashReconUserRows(updated);
  };

  const showQ2 = q1 === "Yes";
  const showNoSbpFromQ1 = q1 === "No";
  const showNoSbpFromQ2 = q1 === "Yes" && q2 === "No";
  const showQ3ToQ6 = q1 === "Yes" && q2 === "Yes";
  const showQ4b = showQ3ToQ6 && q4 === "Yes";

  const workspaceNameEbe = "Employee Benefits Expense";

  useEffect(() => {
    const loadPhase = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const d = result.data;
            let phase = "Execution";
            if (d.Planning?.[workspaceNameEbe]) phase = "Planning";
            else if (d.Execution?.[workspaceNameEbe]) phase = "Execution";
            else if (d.ConcludingProcedures?.[workspaceNameEbe]) phase = "ConcludingProcedures";
            setActualPhase(phase);
            return;
          }
        }
      } catch {
        /* fallback */
      }
      setActualPhase("Execution");
    };
    loadPhase();
  }, []);

  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  useEffect(() => {
    const loadIndAs102Saved = async () => {
      if (!companyName) return;
      const phaseFormatted =
        actualPhase === "ConcludingProcedures"
          ? "ConcludingProcedures"
          : actualPhase.charAt(0).toUpperCase() + actualPhase.slice(1).toLowerCase();
      const fileName = `${phaseFormatted}_${EBE_WORKSPACE_PREFIX}_Responses.json`;
      const esopReady = esopLoadStatus === "loaded" || esopLoadStatus === "error";
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const saved = result.data.substantiveProcedures[IND_AS_102_QUESTION_ID];
          if (saved) {
            localStorage.removeItem(`textbox_${IND_AS_102_QUESTION_ID}`);
            localStorage.removeItem(`documents_${IND_AS_102_QUESTION_ID}`);

            const pr = saved.sbpProcedureResponses as Partial<SbpProcedureResponsesV1> | undefined;
            const ans = saved.indAs102Answers as
              | { q1?: string; q2?: string; q3?: string; q4?: string; q4b?: string; q5?: string; q6?: string }
              | undefined;
            const qSource = pr ?? ans;
            if (qSource) {
              if (qSource.q1 != null) setQ1(qSource.q1);
              if (qSource.q2 != null) setQ2(qSource.q2);
              if (qSource.q3 != null) setQ3(qSource.q3);
              if (qSource.q4 != null) setQ4(qSource.q4);
              if (qSource.q4b != null) setQ4b(qSource.q4b);
              if (qSource.q5 != null) setQ5(qSource.q5);
              if (qSource.q6 != null) setQ6(qSource.q6);
            }

            if (esopReady && pr) {
              if (pr.reportingDate != null) setReportingDate(pr.reportingDate);
              else {
                const legacyRd = (pr as { equityInputs?: { reportingDate?: string } }).equityInputs?.reportingDate;
                if (legacyRd != null) setReportingDate(legacyRd);
              }

              const ea = pr.equityAssessment;
              if (ea) {
                if (ea.eq1 != null) setEq1(ea.eq1);
                if (ea.eq1b != null) setEq1b(ea.eq1b);
                if (ea.eq1Document != null) setEq1Document(ea.eq1Document);
                if (ea.eq2 != null) setEq2(ea.eq2);
                if (ea.eq2Document != null) setEq2Document(ea.eq2Document);
              }

              const ca = pr.cashAlternative;
              if (ca) {
                if (ca.cashAltCounterparty != null) setCashAltCounterparty(ca.cashAltCounterparty);
                if (ca.cashAltFairValueEqual != null) setCashAltFairValueEqual(ca.cashAltFairValueEqual);
                if (ca.cashAltDocument != null) setCashAltDocument(ca.cashAltDocument);
              }

              const ei = pr.equityInputs;
              if (ei) {
                if (ei.selectedFile != null) setSelectedFile(ei.selectedFile);
                if (ei.referenceValue != null) setReferenceValue(ei.referenceValue);
                if (ei.columnMappings && typeof ei.columnMappings === "object") setColumnMappings({ ...ei.columnMappings });
                if (ei.exercisePeriodUnit != null) setExercisePeriodUnit(ei.exercisePeriodUnit);
                if (ei.exercisePeriodValue != null) setExercisePeriodValue(ei.exercisePeriodValue);
                if (ei.tableRowOverrides && typeof ei.tableRowOverrides === "object") setTableRowOverrides({ ...ei.tableRowOverrides });
                if (ei.unvestedLapsedCy != null) setUnvestedLapsedCy(ei.unvestedLapsedCy);
                if (Array.isArray(ei.selectedPartyNamesForLapse)) {
                  setSelectedPartyNamesForLapse([...ei.selectedPartyNamesForLapse]);
                }
                if (Array.isArray(ei.reconUserRows)) {
                  setReconUserRows(
                    ei.reconUserRows.map((r) => ({ reason: r.reason ?? "", amount: Number(r.amount) || 0 }))
                  );
                }
                if (Array.isArray(ei.equitySelectedExpenseLedgers)) {
                  setEquitySelectedExpenseLedgers([...ei.equitySelectedExpenseLedgers]);
                }
              }

              const ci = pr.cashInputs;
              if (ci) {
                if (ci.cashSelectedFile != null) setCashSelectedFile(ci.cashSelectedFile);
                if (ci.cashReferenceValue != null) setCashReferenceValue(ci.cashReferenceValue);
                if (ci.cashColumnMappings && typeof ci.cashColumnMappings === "object") {
                  setCashColumnMappings({ ...ci.cashColumnMappings });
                }
                if (ci.cashExercisePeriodUnit != null) setCashExercisePeriodUnit(ci.cashExercisePeriodUnit);
                if (ci.cashExercisePeriodValue != null) setCashExercisePeriodValue(ci.cashExercisePeriodValue);
                if (ci.cashTableRowOverrides && typeof ci.cashTableRowOverrides === "object") {
                  setCashTableRowOverrides({ ...ci.cashTableRowOverrides });
                }
                if (ci.cashUnvestedLapsedCy != null) setCashUnvestedLapsedCy(ci.cashUnvestedLapsedCy);
                if (Array.isArray(ci.cashSelectedPartyNamesForLapse)) {
                  setCashSelectedPartyNamesForLapse([...ci.cashSelectedPartyNamesForLapse]);
                }
                if (Array.isArray(ci.cashReconUserRows)) {
                  setCashReconUserRows(
                    ci.cashReconUserRows.map((r) => ({ reason: r.reason ?? "", amount: Number(r.amount) || 0 }))
                  );
                }
                if (Array.isArray(ci.cashSelectedExpenseLedgers)) {
                  setCashSelectedExpenseLedgers([...ci.cashSelectedExpenseLedgers]);
                }
              }

              const sr = pr.sbpReserveUi;
              if (sr) {
                if (sr.sbpReserveAmountAsPerFs !== undefined) setSbpReserveAmountAsPerFs(sr.sbpReserveAmountAsPerFs);
                if (Array.isArray(sr.sbpReserveReconRows)) {
                  setSbpReserveReconRows(
                    sr.sbpReserveReconRows.map((r) => ({
                      particulars: r.particulars ?? "",
                      amount: Number(r.amount) || 0,
                    }))
                  );
                }
              }

              const eqc = pr.equityReconciliationComputed;
              if (eqc) {
                if (eqc.expenseAsPerPLSum != null) setExpenseAsPerPLSum(Number(eqc.expenseAsPerPLSum) || 0);
                if (eqc.expenseAsPerESOPWorking != null) setExpenseAsPerESOPWorking(Number(eqc.expenseAsPerESOPWorking) || 0);
                if (eqc.expenseBookedUnvestedLapsed != null) {
                  setExpenseBookedUnvestedLapsed(Number(eqc.expenseBookedUnvestedLapsed) || 0);
                }
                if (eqc.netExpenseToPL != null) setNetExpenseToPL(Number(eqc.netExpenseToPL) || 0);
              }

              const cqc = pr.cashReconciliationComputed;
              if (cqc) {
                if (cqc.cashExpenseAsPerPLSum != null) setCashExpenseAsPerPLSum(Number(cqc.cashExpenseAsPerPLSum) || 0);
                if (cqc.cashExpenseAsPerESOPWorking != null) {
                  setCashExpenseAsPerESOPWorking(Number(cqc.cashExpenseAsPerESOPWorking) || 0);
                }
                if (cqc.cashExpenseBookedUnvestedLapsed != null) {
                  setCashExpenseBookedUnvestedLapsed(Number(cqc.cashExpenseBookedUnvestedLapsed) || 0);
                }
                if (cqc.cashNetExpenseToPL != null) setCashNetExpenseToPL(Number(cqc.cashNetExpenseToPL) || 0);
              }
            }

            let textResponse: { html: string; plainText: string } | null = null;
            if (saved.textResponse) {
              textResponse = {
                html: saved.textResponse.html || saved.textResponse.plainText || "",
                plainText:
                  saved.textResponse.plainText ||
                  (saved.textResponse.html ? String(saved.textResponse.html).replace(/<[^>]*>/g, "") : ""),
              };
              if (textResponse.html) {
                localStorage.setItem(`textbox_${IND_AS_102_QUESTION_ID}`, textResponse.html);
              }
            }
            const rawDocs = saved.documents || [];
            const documents = rawDocs
              .map((d: unknown) => {
                const name = typeof d === "string" ? d : (d as { name?: string })?.name || "";
                if (!name.trim()) return null;
                return { id: `doc_${name}`, type: "linked" as const, name };
              })
              .filter(Boolean) as Array<{ id: string; type: "linked"; name: string }>;
            localStorage.setItem(`documents_${IND_AS_102_QUESTION_ID}`, JSON.stringify(documents));
            setInd102QuestionResponse({ textResponse, documents });
            return;
          }
        }
        localStorage.removeItem(`textbox_${IND_AS_102_QUESTION_ID}`);
        localStorage.removeItem(`documents_${IND_AS_102_QUESTION_ID}`);
        setInd102QuestionResponse({});
      } catch {
        setInd102QuestionResponse({});
      }
    };
    void loadIndAs102Saved();
  }, [companyName, actualPhase, esopLoadStatus]);

  useEffect(() => {
    const loadInferenceSaved = async () => {
      if (!companyName) return;
      const phaseFormatted =
        actualPhase === "ConcludingProcedures"
          ? "ConcludingProcedures"
          : actualPhase.charAt(0).toUpperCase() + actualPhase.slice(1).toLowerCase();
      const fileName = `${phaseFormatted}_${EBE_WORKSPACE_PREFIX}_Responses.json`;
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const saved = result.data.substantiveProcedures[IND_AS_102_INFERENCE_QUESTION_ID];
          if (saved) {
            localStorage.removeItem(`textbox_${IND_AS_102_INFERENCE_QUESTION_ID}`);
            localStorage.removeItem(`documents_${IND_AS_102_INFERENCE_QUESTION_ID}`);
            let textResponse: { html: string; plainText: string } | null = null;
            if (saved.textResponse) {
              textResponse = {
                html: saved.textResponse.html || saved.textResponse.plainText || "",
                plainText:
                  saved.textResponse.plainText ||
                  (saved.textResponse.html ? String(saved.textResponse.html).replace(/<[^>]*>/g, "") : ""),
              };
              if (textResponse.html) {
                localStorage.setItem(`textbox_${IND_AS_102_INFERENCE_QUESTION_ID}`, textResponse.html);
              }
            }
            const documents = (saved.documents || []).map((docName: string) => ({
              id: `doc_${docName}`,
              type: "linked" as const,
              name: docName,
            }));
            localStorage.setItem(`documents_${IND_AS_102_INFERENCE_QUESTION_ID}`, JSON.stringify(documents));
            setInferenceQuestionResponse({ textResponse, documents });
            return;
          }
        }
        localStorage.removeItem(`textbox_${IND_AS_102_INFERENCE_QUESTION_ID}`);
        localStorage.removeItem(`documents_${IND_AS_102_INFERENCE_QUESTION_ID}`);
        setInferenceQuestionResponse({});
      } catch {
        setInferenceQuestionResponse({});
      }
    };
    void loadInferenceSaved();
  }, [companyName, actualPhase]);

  const saveIndAs102Assessment = useCallback(async () => {
    if (!companyName) {
      setInd102SaveError("Company name is required");
      setInd102SaveStatus("error");
      return;
    }
    setInd102Saving(true);
    setInd102SaveStatus("saving");
    setInd102SaveError(null);
    try {
      const phaseFormatted =
        actualPhase === "ConcludingProcedures"
          ? "ConcludingProcedures"
          : actualPhase.charAt(0).toUpperCase() + actualPhase.slice(1).toLowerCase();
      const fileName = `${phaseFormatted}_${EBE_WORKSPACE_PREFIX}_Responses.json`;

      const documentManagers = document.querySelectorAll("[data-document-manager]");
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute("data-item-id");
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === "function") {
            autoSaveMethod();
          }
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));

      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch {
        /* start fresh */
      }
      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }
      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const state = ind102QuestionResponse || { documents: [] };
      const textboxFromLocalStorage = localStorage.getItem(`textbox_${IND_AS_102_QUESTION_ID}`);
      const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

      const sectionToFind = `${phaseFormatted}_${EBE_WORKSPACE_PREFIX}_${IND_AS_102_QUESTION_ID}`;
      let documentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            documentNames = dbResult.data
              .map((file: any) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (e) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, e);
      }

      let textResponse: { html: string; plainText: string } = { html: "", plainText: "" };
      if (hasTextboxContent) {
        textResponse = {
          html: textboxFromLocalStorage as string,
          plainText: (textboxFromLocalStorage as string).replace(/<[^>]*>/g, ""),
        };
      } else if (state.textResponse) {
        textResponse = state.textResponse;
      }

      const sbpProcedureResponses: SbpProcedureResponsesV1 = {
        q1,
        q2,
        q3,
        q4,
        q4b,
        q5,
        q6,
        reportingDate,
        equityAssessment: {
          eq1,
          eq1b,
          eq1Document,
          eq2,
          eq2Document,
        },
        cashAlternative: {
          cashAltCounterparty,
          cashAltFairValueEqual,
          cashAltDocument,
        },
        equityInputs: {
          selectedFile,
          referenceValue,
          columnMappings,
          exercisePeriodUnit,
          exercisePeriodValue,
          tableRowOverrides,
          unvestedLapsedCy,
          selectedPartyNamesForLapse,
          reconUserRows,
          equitySelectedExpenseLedgers,
        },
        cashInputs: {
          cashSelectedFile,
          cashReferenceValue,
          cashColumnMappings,
          cashExercisePeriodUnit,
          cashExercisePeriodValue,
          cashTableRowOverrides,
          cashUnvestedLapsedCy,
          cashSelectedPartyNamesForLapse,
          cashReconUserRows,
          cashSelectedExpenseLedgers,
        },
        sbpReserveUi: {
          sbpReserveAmountAsPerFs,
          sbpReserveReconRows,
        },
        equityReconciliationComputed: {
          expenseAsPerPLSum,
          expenseAsPerESOPWorking,
          expenseBookedUnvestedLapsed,
          netExpenseToPL,
        },
        cashReconciliationComputed: {
          cashExpenseAsPerPLSum,
          cashExpenseAsPerESOPWorking,
          cashExpenseBookedUnvestedLapsed,
          cashNetExpenseToPL,
        },
      };

      existingData.substantiveProcedures[IND_AS_102_QUESTION_ID] = {
        selectedOption: "",
        textResponse,
        documents: documentNames,
        table: [],
        indAs102Answers: { q1, q2, q3, q4, q4b, q5, q6 },
        sbpProcedureResponses,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `${phaseFormatted} ${EBE_WORKSPACE_PREFIX} Substantive Responses`,
        folderName: "juggernaut",
        fyYear,
        sectionList: [`${phaseFormatted}_${EBE_WORKSPACE_PREFIX}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save Ind AS 102 assessment");
      }

      setInd102SaveStatus("saved");
      setTimeout(() => setInd102SaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save Ind AS 102 assessment:", error);
      setInd102SaveStatus("error");
      setInd102SaveError(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setInd102Saving(false);
    }
  }, [
    companyName,
    actualPhase,
    ind102QuestionResponse,
    q1,
    q2,
    q3,
    q4,
    q4b,
    q5,
    q6,
    reportingDate,
    eq1,
    eq1b,
    eq1Document,
    eq2,
    eq2Document,
    cashAltCounterparty,
    cashAltFairValueEqual,
    cashAltDocument,
    selectedFile,
    referenceValue,
    columnMappings,
    exercisePeriodUnit,
    exercisePeriodValue,
    tableRowOverrides,
    unvestedLapsedCy,
    selectedPartyNamesForLapse,
    reconUserRows,
    equitySelectedExpenseLedgers,
    cashSelectedFile,
    cashReferenceValue,
    cashColumnMappings,
    cashExercisePeriodUnit,
    cashExercisePeriodValue,
    cashTableRowOverrides,
    cashUnvestedLapsedCy,
    cashSelectedPartyNamesForLapse,
    cashReconUserRows,
    cashSelectedExpenseLedgers,
    sbpReserveAmountAsPerFs,
    sbpReserveReconRows,
    expenseAsPerPLSum,
    expenseAsPerESOPWorking,
    expenseBookedUnvestedLapsed,
    netExpenseToPL,
    cashExpenseAsPerPLSum,
    cashExpenseAsPerESOPWorking,
    cashExpenseBookedUnvestedLapsed,
    cashNetExpenseToPL,
  ]);

  const saveInferenceQuestion = useCallback(async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus("error");
      return;
    }
    setInferenceSaving(true);
    setInferenceSaveStatus("saving");
    setInferenceSaveError(null);
    try {
      const phaseFormatted =
        actualPhase === "ConcludingProcedures"
          ? "ConcludingProcedures"
          : actualPhase.charAt(0).toUpperCase() + actualPhase.slice(1).toLowerCase();
      const fileName = `${phaseFormatted}_${EBE_WORKSPACE_PREFIX}_Responses.json`;

      const documentManagers = document.querySelectorAll("[data-document-manager]");
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute("data-item-id");
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === "function") {
            autoSaveMethod();
          }
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));

      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch {
        /* start fresh */
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }
      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const state = inferenceQuestionResponse || { documents: [] };
      const textboxFromLocalStorage = localStorage.getItem(`textbox_${IND_AS_102_INFERENCE_QUESTION_ID}`);
      const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

      const sectionToFind = `${phaseFormatted}_${EBE_WORKSPACE_PREFIX}_${IND_AS_102_INFERENCE_QUESTION_ID}`;
      let documentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            documentNames = dbResult.data
              .map((file: any) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (e) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, e);
      }

      let textResponse: { html: string; plainText: string } = { html: "", plainText: "" };
      if (hasTextboxContent) {
        textResponse = {
          html: textboxFromLocalStorage as string,
          plainText: (textboxFromLocalStorage as string).replace(/<[^>]*>/g, ""),
        };
      } else if (state.textResponse) {
        textResponse = state.textResponse;
      }

      existingData.substantiveProcedures[IND_AS_102_INFERENCE_QUESTION_ID] = {
        selectedOption: "",
        textResponse,
        documents: documentNames,
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `${phaseFormatted} ${EBE_WORKSPACE_PREFIX} Substantive Responses`,
        folderName: "juggernaut",
        fyYear,
        sectionList: [`${phaseFormatted}_${EBE_WORKSPACE_PREFIX}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference and conclusion");
      }

      setInferenceSaveStatus("saved");
      setTimeout(() => setInferenceSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to save inference and conclusion:", error);
      setInferenceSaveStatus("error");
      setInferenceSaveError(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setInferenceSaving(false);
    }
  }, [companyName, actualPhase, inferenceQuestionResponse]);

  const showEquitySection = showQ3ToQ6 && q4 === "Yes";
  const showCashSection = showQ3ToQ6 && q5 === "Yes";
  const showEq1b = showEquitySection && eq1 === "Yes";
  const showEq1Document = showEquitySection && eq1 === "Yes" && eq1b === "No";
  const showEq2Document = showEquitySection && eq2 === "No";

  const searchForEsopFiles = async () => {
    setFileSearchStatus("searching");
    try {
      // Look for files with section ["Execution_EBE_ESOP"] in client folder
      if ((window as any).sharePointAPI?.findFilesBySection) {
        const result = await (window as any).sharePointAPI.findFilesBySection(ESOP_SECTION, "client");
        if (result?.success && result?.data?.files) {
          const raw = result.data.files;
          const files = (Array.isArray(raw) ? raw : []).map((f: any) => (typeof f === "string" ? f : f?.name)).filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFoundFiles([]);
            setFileSearchStatus("not_found");
          }
        } else {
          setFoundFiles([]);
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFoundFiles([]);
      setFileSearchStatus("not_found");
    }
  };

  useEffect(() => {
    if (showEquitySection) {
      searchForEsopFiles();
    }
  }, [showEquitySection]);

  const searchForCashEsopFiles = async () => {
    setCashFileSearchStatus("searching");
    try {
      // Look for files with section ["Execution_EBE_ESOP_Cash"] in client folder
      if ((window as any).sharePointAPI?.findFilesBySection) {
        const result = await (window as any).sharePointAPI.findFilesBySection(ESOP_CASH_SECTION, "client");
        if (result?.success && result?.data?.files) {
          const raw = result.data.files;
          const files = (Array.isArray(raw) ? raw : []).map((f: any) => (typeof f === "string" ? f : f?.name)).filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setCashFoundFiles(files);
            setCashFileSearchStatus("found");
          } else {
            setCashFoundFiles([]);
            setCashFileSearchStatus("not_found");
          }
        } else {
          setCashFoundFiles([]);
          setCashFileSearchStatus("not_found");
        }
      } else {
        setCashFileSearchStatus("not_found");
      }
    } catch {
      setCashFoundFiles([]);
      setCashFileSearchStatus("not_found");
    }
  };

  useEffect(() => {
    if (showCashSection) {
      searchForCashEsopFiles();
    }
  }, [showCashSection]);

  // Load ESOP table data from SharePoint when page mounts (show table if file exists)
  useEffect(() => {
    loadEsopJson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadEsopFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(",")[1] || "");
              };
              reader.onerror = () => reject(new Error("Failed to read file"));
              reader.readAsDataURL(file);
            });

            const fyYear = getFY_YEAR();
            if (!fyYear) {
              setUploadStatus("error");
              return;
            }

            if ((window as any).sharePointAPI?.uploadFile) {
              const result = await (window as any).sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue || "ESOP Schedule",
                folderName: "client",
                fyYear,
                sectionList: [ESOP_SECTION],
              });

              if (result?.success) {
                setSelectedFile(file.name);
                setFoundFiles((prev) => (prev.includes(file.name) ? prev : [file.name, ...prev]));
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if ((window as any).payroll?.loadExcelColumns) {
        const result = await (window as any).payroll.loadExcelColumns(selectedFile);
        if (result?.ok && result?.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns(columns);
        }
      } else {
        const testCols = ESOP_FIELD_KEYS.map((f) => f.label);
        setExcelColumns(testCols.map((col) => ({ name: col, value: col })));
      }
    } catch {
      const testCols = ESOP_FIELD_KEYS.map((f) => f.label);
      setExcelColumns(testCols.map((col) => ({ name: col, value: col })));
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  const handleMappingChange = (fieldKey: string, columnName: string) => {
    setColumnMappings((prev) => ({ ...prev, [fieldKey]: columnName }));
  };

  const allEsopFieldsMapped = ESOP_FIELD_KEYS.every(
    (f) => columnMappings[f.key] && columnMappings[f.key].length > 0
  );

  const uploadCashEsopFile = async () => {
    setCashUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(",")[1] || "");
              };
              reader.onerror = () => reject(new Error("Failed to read file"));
              reader.readAsDataURL(file);
            });
            const fyYear = getFY_YEAR();
            if (!fyYear) {
              setCashUploadStatus("error");
              return;
            }
            if ((window as any).sharePointAPI?.uploadFile) {
              const result = await (window as any).sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: cashReferenceValue || "ESOP Schedule (Cash)",
                folderName: "client",
                fyYear,
                sectionList: [ESOP_CASH_SECTION],
              });
              if (result?.success) {
                setCashSelectedFile(file.name);
                setCashFoundFiles((prev) => (prev.includes(file.name) ? prev : [file.name, ...prev]));
                setCashFileSearchStatus("found");
                setCashUploadStatus("completed");
              } else {
                setCashUploadStatus("error");
              }
            } else {
              setCashUploadStatus("error");
            }
          } catch {
            setCashUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setCashUploadStatus("error");
    }
  };

  const loadCashExcelColumns = async () => {
    if (!cashSelectedFile) return;
    setCashIsLoadingExcelColumns(true);
    try {
      if ((window as any).payroll?.loadExcelColumns) {
        const result = await (window as any).payroll.loadExcelColumns(cashSelectedFile);
        if (result?.ok && result?.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setCashExcelColumns(columns);
        }
      } else {
        setCashExcelColumns(CASH_ESOP_FIELD_KEYS.map((f) => ({ name: f.label, value: f.key })));
      }
    } catch {
      setCashExcelColumns(CASH_ESOP_FIELD_KEYS.map((f) => ({ name: f.label, value: f.key })));
    } finally {
      setCashIsLoadingExcelColumns(false);
    }
  };

  const handleCashMappingChange = (fieldKey: string, columnName: string) => {
    setCashColumnMappings((prev) => ({ ...prev, [fieldKey]: columnName }));
  };

  const allCashEsopFieldsMapped = CASH_ESOP_FIELD_KEYS.every(
    (f) => cashColumnMappings[f.key] && cashColumnMappings[f.key].length > 0
  );

  const loadEsopJson = async () => {
    setEsopLoadStatus("loading");
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(ESOP_JSON_FILE);
      if (result?.success && result?.data) {
        const data = result.data as ESOPJson;
        setEsopJson(data);

        // SBP Reserve
        if (data.sbp_reserve) {
          if (data.sbp_reserve.amount_as_per_fs != null) setSbpReserveAmountAsPerFs(Number(data.sbp_reserve.amount_as_per_fs));
          const rows = data.sbp_reserve.reconciliation_rows;
          setSbpReserveReconRows(Array.isArray(rows) ? rows.map((r) => ({ particulars: r.particulars || "", amount: Number(r.amount) || 0 })) : []);
        }

        // Equity ESOP Reconciliation – from equity_sbp.recomputation
        const eqRecom = data.equity_sbp?.recomputation as any;
        if (eqRecom) {
          if (eqRecom.expense_as_per_profit_and_loss_D != null) {
            setExpenseAsPerPLSum(Number(eqRecom.expense_as_per_profit_and_loss_D) || 0);
          }
          if (eqRecom.expense_as_per_esop_working_A != null) {
            setExpenseAsPerESOPWorking(Number(eqRecom.expense_as_per_esop_working_A) || 0);
          }
          if (eqRecom.expense_booked_till_date_on_unvested_lapsed_B != null) {
            setExpenseBookedUnvestedLapsed(Number(eqRecom.expense_booked_till_date_on_unvested_lapsed_B) || 0);
          }
          if (eqRecom.net_expense_to_profit_and_loss_C != null) {
            setNetExpenseToPL(Number(eqRecom.net_expense_to_profit_and_loss_C) || 0);
          } else if (eqRecom.expense_as_per_esop_working_A != null && eqRecom.expense_booked_till_date_on_unvested_lapsed_B != null) {
            setNetExpenseToPL(
              Number(eqRecom.expense_as_per_esop_working_A) - Number(eqRecom.expense_booked_till_date_on_unvested_lapsed_B)
            );
          }
          const eqRows = eqRecom.reconciliation_rows;
          setReconUserRows(
            Array.isArray(eqRows)
              ? eqRows.map((r: any) => ({ reason: r.reason || r.particulars || "", amount: Number(r.amount) || 0 }))
              : []
          );
        }

        // Cash ESOP Reconciliation – from cash_sbp.recomputation
        const cashRecom = data.cash_sbp?.recomputation as any;
        if (cashRecom) {
          if (cashRecom.expense_as_per_profit_and_loss_D != null) {
            setCashExpenseAsPerPLSum(Number(cashRecom.expense_as_per_profit_and_loss_D) || 0);
          }
          if (cashRecom.expense_as_per_esop_working_A != null) {
            setCashExpenseAsPerESOPWorking(Number(cashRecom.expense_as_per_esop_working_A) || 0);
          }
          if (cashRecom.expense_booked_till_date_on_unvested_lapsed_B != null) {
            setCashExpenseBookedUnvestedLapsed(Number(cashRecom.expense_booked_till_date_on_unvested_lapsed_B) || 0);
          }
          if (cashRecom.net_expense_to_profit_and_loss_C != null) {
            setCashNetExpenseToPL(Number(cashRecom.net_expense_to_profit_and_loss_C) || 0);
          } else if (cashRecom.expense_as_per_esop_working_A != null && cashRecom.expense_booked_till_date_on_unvested_lapsed_B != null) {
            setCashNetExpenseToPL(
              Number(cashRecom.expense_as_per_esop_working_A) - Number(cashRecom.expense_booked_till_date_on_unvested_lapsed_B)
            );
          }
          const cashRows = cashRecom.reconciliation_rows;
          setCashReconUserRows(
            Array.isArray(cashRows)
              ? cashRows.map((r: any) => ({ reason: r.reason || r.particulars || "", amount: Number(r.amount) || 0 }))
              : []
          );
        }

        setEsopLoadStatus("loaded");
      } else {
        setEsopJson(null);
        setEsopLoadStatus("error");
      }
    } catch {
      setEsopJson(null);
      setEsopLoadStatus("error");
    }
  };

  // Load Expense-type ledgers for Expense as per Profit & Loss (D)
  useEffect(() => {
    const loadExpenseLedgers = async () => {
      try {
        if (!(window as any).sharePointAPI?.downloadTrialBalanceMapping) return;
        const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (!mappingResult?.success || !mappingResult?.data) return;
        const mappingData = mappingResult.data;
        const ledgerMap = new Map<string, number>();

        const parseDate = (dateStr: string): Date => {
          const [day, month, year] = dateStr.split("/");
          return new Date(2000 + parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        };

        for (const [, ledgers] of Object.entries(mappingData)) {
          if (!Array.isArray(ledgers)) continue;
          for (const item of ledgers as any[]) {
            if (item?.type !== "Expense") continue;
            const amountDict = item.amount || {};
            const dates = Object.keys(amountDict || {});
            if (!dates.length) continue;
            const latestDate = dates.reduce((a, b) => (parseDate(a) > parseDate(b) ? a : b), dates[0]);
            const latestAmount = parseFloat(String(amountDict[latestDate])) || 0;
            let ledgerName = (item.ledger_name || item.ledgerName || "").toString();
            ledgerName = ledgerName.replace(/_x000D/g, "").replace(/\n/g, "").replace(/_+$/, "").trim();
            if (!ledgerName) continue;
            ledgerMap.set(ledgerName, (ledgerMap.get(ledgerName) || 0) + latestAmount);
          }
        }

        const options = Array.from(ledgerMap.entries())
          .map(([name, amount]) => ({ name, amount }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setEquityExpenseLedgerOptions(options);
      } catch {
        // ignore errors; dropdown will just be empty
      }
    };

    loadExpenseLedgers();
  }, []);

  // When user selects ledgers, recompute Expense as per P&L (D)
  useEffect(() => {
    if (!equityExpenseLedgerOptions.length || !equitySelectedExpenseLedgers.length) return;
    const sum = equityExpenseLedgerOptions
      .filter((o) => equitySelectedExpenseLedgers.includes(o.name))
      .reduce((s, o) => s + o.amount, 0);
    setExpenseAsPerPLSum(sum);
  }, [equityExpenseLedgerOptions, equitySelectedExpenseLedgers]);

  // Cash: when user selects ledgers, recompute Expense as per P&L (D)
  useEffect(() => {
    if (!equityExpenseLedgerOptions.length || !cashSelectedExpenseLedgers.length) return;
    const sum = equityExpenseLedgerOptions
      .filter((o) => cashSelectedExpenseLedgers.includes(o.name))
      .reduce((s, o) => s + o.amount, 0);
    setCashExpenseAsPerPLSum(sum);
  }, [equityExpenseLedgerOptions, cashSelectedExpenseLedgers]);

  /** Fetch total for note line 30105 (SBP Reserve) from trial balance mapping – same pattern as Capital Advances */
  const loadNoteLine30105Total = async () => {
    setSbpReserveLoadFsStatus("loading");
    try {
      if ((window as any).sharePointAPI?.downloadTrialBalanceMapping) {
        const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (mappingResult?.success && mappingResult?.data) {
          const mappingData = mappingResult.data;
          let total = 0;
          for (const [, ledgers] of Object.entries(mappingData)) {
            if (!Array.isArray(ledgers)) continue;
            for (const item of ledgers) {
              const noteLineId = item.note_line_id ?? item.noteLineId;
              if (Number(noteLineId) !== NOTE_LINE_SBP_RESERVE) continue;
              const amountDict = item.amount || {};
              if (typeof amountDict !== "object" || Object.keys(amountDict).length === 0) continue;
              const parseDate = (dateStr: string): Date => {
                const parts = dateStr.split("/");
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = 2000 + parseInt(parts[2], 10);
                return new Date(year, month, day);
              };
              const dates = Object.keys(amountDict);
              const latestDate = dates.reduce((a, b) => (parseDate(a) > parseDate(b) ? a : b), dates[0]);
              total += parseFloat(amountDict[latestDate]) || 0;
            }
          }
          setSbpReserveAmountAsPerFs(total);
          setSbpReserveLoadFsStatus("loaded");
        } else {
          setSbpReserveLoadFsStatus("error");
        }
      } else {
        setSbpReserveLoadFsStatus("error");
      }
    } catch {
      setSbpReserveLoadFsStatus("error");
    }
  };

  const runAnalysis = async () => {
    // If we don't have the payroll runner available, just try to reload JSON
    if (!(window as any).payroll?.run) {
      await loadEsopJson();
      return;
    }

    // We need a selected file and all ESOP fields mapped plus exercise period and reporting date
    if (!selectedFile || !allEsopFieldsMapped || !exercisePeriodUnit || !exercisePeriodValue || !reportingDate) {
      await loadEsopJson();
      return;
    }

    try {
      const cleanFileName = selectedFile.includes(" (") ? selectedFile.split(" (")[0] : selectedFile;

      const options = {
        // Excel file in client folder to use as ESOP schedule
        payroll_file: cleanFileName,
        excel_file_name: cleanFileName,
        // Column mappings from Step 2: Map Columns
        party_name: columnMappings["employee_party_name"],
        no_share: columnMappings["number_of_shares"],
        grant_date: columnMappings["grant_date"],
        period_from: columnMappings["period_from"],
        period_to: columnMappings["period_to"],
        ex_price: columnMappings["exercise_price"],
        fair_val: columnMappings["fair_value_sbp_reserve"],
        // Exercise period and reporting date
        unit_type: exercisePeriodUnit.toLowerCase(),
        unit_time: Number(exercisePeriodValue) || 0,
        reporting_date: reportingDate, // backend reads and converts to datetime
        // Target JSON in juggernaut folder
        json_name: ESOP_JSON_FILE,
      };

      // Execute backend ESOP equity computation (Python script) via payroll runner
      const result = await (window as any).payroll.run("execute_share_based_payment_equity", {
        inputFiles: [],
        options,
      });

      if (result?.ok) {
        // After successful backend run, reload the ESOP JSON (equity_sbp updated there)
        await loadEsopJson();
      } else {
        // If backend failed, keep existing data but mark load as error
        setEsopLoadStatus("error");
      }
    } catch {
      setEsopLoadStatus("error");
    }
  };

  const runCashAnalysis = async () => {
    // If runner is not available, just reload JSON
    if (!(window as any).payroll?.run) {
      await loadEsopJson();
      return;
    }

    // Require file, mappings, exercise period, and reporting date
    if (!cashSelectedFile || !allCashEsopFieldsMapped || !cashExercisePeriodUnit || !cashExercisePeriodValue || !reportingDate) {
      await loadEsopJson();
      return;
    }

    try {
      const cleanFileName = cashSelectedFile.includes(" (") ? cashSelectedFile.split(" (")[0] : cashSelectedFile;

      const options = {
        // Excel file in client folder for cash-settled ESOP schedule
        payroll_file: cleanFileName,
        excel_file_name: cleanFileName,
        // Column mappings from Cash Step 2: Map Columns
        party_name: cashColumnMappings["employee_party_name"],
        no_share: cashColumnMappings["number_of_shares"],
        grant_date: cashColumnMappings["grant_date"],
        period_from: cashColumnMappings["period_from"],
        period_to: cashColumnMappings["period_to"],
        // Cash mapping does not have a separate Exercise Price column in UI
        ex_price: "",
        fair_val: cashColumnMappings["fair_value_sbp_reserve"],
        // Exercise period and reporting date
        unit_type: cashExercisePeriodUnit.toLowerCase(),
        unit_time: Number(cashExercisePeriodValue) || 0,
        reporting_date: reportingDate,
        // Target JSON in juggernaut folder
        json_name: ESOP_JSON_FILE,
      };

      const result = await (window as any).payroll.run("execute_share_based_payment_cash", {
        inputFiles: [],
        options,
      });

      if (result?.ok) {
        await loadEsopJson();
      } else {
        setEsopLoadStatus("error");
      }
    } catch {
      setEsopLoadStatus("error");
    }
  };

  const reportingDateForRange = useMemo(() => {
    const fromJson = esopJson?.equity_sbp?.reporting_date;
    if (fromJson) return fromJson;
    if (!reportingDate) return "";
    const [y, m, d] = reportingDate.split("-");
    return `${d}-${m}-${y}`;
  }, [esopJson?.equity_sbp?.reporting_date, reportingDate]);

  const twoYearRange = useMemo(
    () => (reportingDateForRange ? getTwoYearRangeFromReportingDate(reportingDateForRange) : null),
    [reportingDateForRange]
  );

  const filteredTableRows = useMemo(() => {
    const data = esopJson?.equity_sbp?.data;
    if (!data || !Array.isArray(data) || !twoYearRange) return [];
    return data.filter((row) => {
      const periodTo = parseDDMMYYYY(row.period_to);
      return isDateInRange(periodTo, twoYearRange.start, twoYearRange.end);
    });
  }, [esopJson?.equity_sbp?.data, twoYearRange]);

  /** Unique party/employee names from full equity_sbp.data (for unvested lapsed dropdown) */
  const allPartyNames = useMemo(() => {
    const data = esopJson?.equity_sbp?.data;
    if (!data || !Array.isArray(data)) return [];
    const names = new Set<string>();
    data.forEach((row) => {
      if (row.employee_name && String(row.employee_name).trim()) names.add(String(row.employee_name).trim());
    });
    return Array.from(names).sort();
  }, [esopJson?.equity_sbp?.data]);

  /** Show ESOP table, Unvested lapsed, and Reconciliation Summary only when equity_sbp has data (not empty {}) */
  const hasEquitySbpData = useMemo(() => {
    const sbp = esopJson?.equity_sbp;
    return Boolean(
      sbp &&
      typeof sbp === "object" &&
      !Array.isArray(sbp) &&
      Object.keys(sbp).length > 0
    );
  }, [esopJson?.equity_sbp]);

  /** Cash-settled: same when cash_sbp has data (not empty {}) */
  const hasCashSbpData = useMemo(() => {
    const sbp = esopJson?.cash_sbp;
    return Boolean(
      sbp &&
      typeof sbp === "object" &&
      !Array.isArray(sbp) &&
      Object.keys(sbp).length > 0
    );
  }, [esopJson?.cash_sbp]);

  const reportingDateForRangeCash = useMemo(() => {
    const fromJson = esopJson?.cash_sbp?.reporting_date;
    if (fromJson) return fromJson;
    if (!reportingDate) return "";
    const [y, m, d] = reportingDate.split("-");
    return `${d}-${m}-${y}`;
  }, [esopJson?.cash_sbp?.reporting_date, reportingDate]);

  const twoYearRangeCash = useMemo(
    () => (reportingDateForRangeCash ? getTwoYearRangeFromReportingDate(reportingDateForRangeCash) : null),
    [reportingDateForRangeCash]
  );

  const filteredCashTableRows = useMemo(() => {
    const data = esopJson?.cash_sbp?.data;
    if (!data || !Array.isArray(data) || !twoYearRangeCash) return [];
    return data.filter((row) => {
      const periodTo = parseDDMMYYYY(row.period_to);
      return isDateInRange(periodTo, twoYearRangeCash.start, twoYearRangeCash.end);
    });
  }, [esopJson?.cash_sbp?.data, twoYearRangeCash]);

  const cashAllPartyNames = useMemo(() => {
    const data = esopJson?.cash_sbp?.data;
    if (!data || !Array.isArray(data)) return [];
    const names = new Set<string>();
    data.forEach((row) => {
      if (row.employee_name && String(row.employee_name).trim()) names.add(String(row.employee_name).trim());
    });
    return Array.from(names).sort();
  }, [esopJson?.cash_sbp?.data]);

  const getRowKey = (row: ESOPDataRow) => `${row.employee_name}_${row.period_to}_${row.period_from}`;

  const getCyForRow = (row: ESOPDataRow) => tableRowOverrides[getRowKey(row)]?.allotted_in_cy ?? row.allotted_in_cy ?? "";
  const getPyForRow = (row: ESOPDataRow) => tableRowOverrides[getRowKey(row)]?.allotted_in_py ?? row.allotted_in_py ?? "";

  const setCyForRow = (row: ESOPDataRow, value: string) => {
    const key = getRowKey(row);
    setTableRowOverrides((prev) => {
      const cur = prev[key] || { allotted_in_cy: "", allotted_in_py: "" };
      const next = { ...cur, allotted_in_cy: value };
      if (value === "Yes") next.allotted_in_py = "No";
      return { ...prev, [key]: next };
    });
  };

  const setPyForRow = (row: ESOPDataRow, value: string) => {
    const key = getRowKey(row);
    setTableRowOverrides((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { allotted_in_cy: "", allotted_in_py: "" }), allotted_in_py: value },
    }));
  };

  /** Compute remarks from CY/PY and exercise date (same rules as spec) */
  const computeRemarks = (
    cy: string,
    py: string,
    exerciseDateStr: string,
    range: { start: Date; end: Date } | null
  ): string => {
    if (cy === "Yes") return "Allotted in CY";
    if (py === "Yes") return "Allotted in PY";
    if (isExerciseDateInCY(exerciseDateStr, range)) return "Vested & Lapsed";
    if (isExerciseDateInNextYear(exerciseDateStr, range)) return "Vested";
    return "";
  };

  /** Display remarks: from JSON (row.remarks) when no local override; otherwise computed so user sees live result */
  const getRemarksForRow = (row: ESOPDataRow): string => {
    const key = getRowKey(row);
    if (tableRowOverrides[key]) {
      const cy = getCyForRow(row);
      const py = getPyForRow(row);
      return computeRemarks(cy, py, row.exercise_date, twoYearRange);
    }
    return row.remarks ?? "";
  };

  const isPeriodToInCY = (row: ESOPDataRow) => isInCurrentFY(parseDDMMYYYY(row.period_to), twoYearRange);
  const isPyDisabled = (row: ESOPDataRow) => getCyForRow(row) === "Yes" || isPeriodToInCY(row);

  const togglePartyForLapse = (name: string) => {
    setSelectedPartyNamesForLapse((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const runLapseAnalysis = () => {
    // Dummy – user will add functionality later (e.g. total expense for selected party rows)
  };

  const saveEsopTable = async () => {
    if (!esopJson?.equity_sbp?.data) return;
    setEsopSaveStatus("saving");
    try {
      const data = esopJson.equity_sbp.data;
      const updatedData = data.map((row) => {
        const key = getRowKey(row);
        const override = tableRowOverrides[key];
        const cy = override?.allotted_in_cy ?? row.allotted_in_cy ?? "";
        const py = override?.allotted_in_py ?? row.allotted_in_py ?? "";
        const remarks = computeRemarks(cy, py, row.exercise_date, twoYearRange);
        return { ...row, allotted_in_cy: cy, allotted_in_py: py, remarks };
      });
      const updatedJson: ESOPJson = {
        ...esopJson,
        equity_sbp: {
          ...esopJson.equity_sbp,
          data: updatedData,
        },
      };
      const jsonString = JSON.stringify(updatedJson, null, 4);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        setEsopSaveStatus("error");
        return;
      }
      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: ESOP_JSON_FILE,
        referenceValue: "ESOP Table",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_EBE_ESOP"],
      });
      if (result?.success) {
        setEsopJson(updatedJson);
        setTableRowOverrides({});
        setEsopSaveStatus("saved");
        setTimeout(() => setEsopSaveStatus("idle"), 3000);
      } else {
        setEsopSaveStatus("error");
      }
    } catch {
      setEsopSaveStatus("error");
    }
  };

  const getCashCyForRow = (row: ESOPDataRow) => cashTableRowOverrides[getRowKey(row)]?.allotted_in_cy ?? row.allotted_in_cy ?? "";
  const getCashPyForRow = (row: ESOPDataRow) => cashTableRowOverrides[getRowKey(row)]?.allotted_in_py ?? row.allotted_in_py ?? "";
  const setCashCyForRow = (row: ESOPDataRow, value: string) => {
    const key = getRowKey(row);
    setCashTableRowOverrides((prev) => {
      const cur = prev[key] || { allotted_in_cy: "", allotted_in_py: "" };
      const next = { ...cur, allotted_in_cy: value };
      if (value === "Yes") next.allotted_in_py = "No";
      return { ...prev, [key]: next };
    });
  };
  const setCashPyForRow = (row: ESOPDataRow, value: string) => {
    const key = getRowKey(row);
    setCashTableRowOverrides((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { allotted_in_cy: "", allotted_in_py: "" }), allotted_in_py: value },
    }));
  };
  const getCashRemarksForRow = (row: ESOPDataRow): string => {
    const key = getRowKey(row);
    if (cashTableRowOverrides[key]) {
      const cy = getCashCyForRow(row);
      const py = getCashPyForRow(row);
      return computeRemarks(cy, py, row.exercise_date, twoYearRangeCash);
    }
    return row.remarks ?? "";
  };
  const isCashPyDisabled = (row: ESOPDataRow) => getCashCyForRow(row) === "Yes" || isInCurrentFY(parseDDMMYYYY(row.period_to), twoYearRangeCash);

  const saveCashEsopTable = async () => {
    if (!esopJson?.cash_sbp?.data) return;
    setCashEsopSaveStatus("saving");
    try {
      const data = esopJson.cash_sbp.data;
      const updatedData = data.map((row) => {
        const key = getRowKey(row);
        const override = cashTableRowOverrides[key];
        const cy = override?.allotted_in_cy ?? row.allotted_in_cy ?? "";
        const py = override?.allotted_in_py ?? row.allotted_in_py ?? "";
        const remarks = computeRemarks(cy, py, row.exercise_date, twoYearRangeCash);
        return { ...row, allotted_in_cy: cy, allotted_in_py: py, remarks };
      });
      const updatedJson: ESOPJson = {
        ...esopJson,
        cash_sbp: {
          ...esopJson.cash_sbp,
          data: updatedData,
        },
      };
      const jsonString = JSON.stringify(updatedJson, null, 4);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        setCashEsopSaveStatus("error");
        return;
      }
      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: ESOP_JSON_FILE,
        referenceValue: "ESOP Cash Table",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_EBE_ESOP"],
      });
      if (result?.success) {
        setEsopJson(updatedJson);
        setCashTableRowOverrides({});
        setCashEsopSaveStatus("saved");
        setTimeout(() => setCashEsopSaveStatus("idle"), 3000);
      } else {
        setCashEsopSaveStatus("error");
      }
    } catch {
      setCashEsopSaveStatus("error");
    }
  };

  // Save Equity ESOP Reconciliation (recomputation) back to JSON
  const saveEquityRecomputation = async () => {
    if (!esopJson || !esopJson.equity_sbp) return;
    try {
      const current: ESOPJson = esopJson;
      const recom = (current.equity_sbp?.recomputation || {}) as any;
      const diff = differenceCD;
      const netDiff = netDifference;
      const updatedRecom = {
        ...recom,
        expense_as_per_profit_and_loss_D: expenseAsPerPLSum,
        expense_as_per_esop_working_A: expenseAsPerESOPWorking,
        expense_booked_till_date_on_unvested_lapsed_B: expenseBookedUnvestedLapsed,
        net_expense_to_profit_and_loss_C: netExpenseToPL,
        difference_C_minus_D: diff,
        reconciliation_rows: reconUserRows.map((r) => ({ reason: r.reason, amount: r.amount })),
        net_difference: netDiff,
      };
      const updatedJson: ESOPJson = {
        ...current,
        equity_sbp: {
          ...(current.equity_sbp || {}),
          recomputation: updatedRecom as any,
        } as ESOPEquitySbp,
      };
      const jsonString = JSON.stringify(updatedJson, null, 4);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        setEsopSaveStatus("error");
        return;
      }
      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: ESOP_JSON_FILE,
        referenceValue: "ESOP Equity Reconciliation",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_EBE_ESOP"],
      });
      if (result?.success) {
        setEsopJson(updatedJson);
        setEsopSaveStatus("saved");
        setTimeout(() => setEsopSaveStatus("idle"), 3000);
      } else {
        setEsopSaveStatus("error");
      }
    } catch {
      setEsopSaveStatus("error");
    }
  };

  // Save Cash ESOP Reconciliation (recomputation) back to JSON
  const saveCashRecomputation = async () => {
    if (!esopJson || !esopJson.cash_sbp) return;
    try {
      const current: ESOPJson = esopJson;
      const recom = (current.cash_sbp?.recomputation || {}) as any;
      const diff = cashDifferenceCD;
      const netDiff = cashNetDifference;
      const updatedRecom = {
        ...recom,
        expense_as_per_profit_and_loss_D: cashExpenseAsPerPLSum,
        expense_as_per_esop_working_A: cashExpenseAsPerESOPWorking,
        expense_booked_till_date_on_unvested_lapsed_B: cashExpenseBookedUnvestedLapsed,
        net_expense_to_profit_and_loss_C: cashNetExpenseToPL,
        difference_C_minus_D: diff,
        reconciliation_rows: cashReconUserRows.map((r) => ({ reason: r.reason, amount: r.amount })),
        net_difference: netDiff,
      };
      const updatedJson: ESOPJson = {
        ...current,
        cash_sbp: {
          ...(current.cash_sbp || {}),
          recomputation: updatedRecom as any,
        } as ESOPEquitySbp,
      };
      const jsonString = JSON.stringify(updatedJson, null, 4);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        setCashEsopSaveStatus("error");
        return;
      }
      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: ESOP_JSON_FILE,
        referenceValue: "ESOP Cash Reconciliation",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_EBE_ESOP"],
      });
      if (result?.success) {
        setEsopJson(updatedJson);
        setCashEsopSaveStatus("saved");
        setTimeout(() => setCashEsopSaveStatus("idle"), 3000);
      } else {
        setCashEsopSaveStatus("error");
      }
    } catch {
      setCashEsopSaveStatus("error");
    }
  };

  /** SBP Reserve: computed values from JSON + local state */
  const sbpReserveRecom = esopJson?.sbp_reserve?.amount_as_per_recomputation;
  const sbpReserveClosing = sbpReserveRecom?.closing_sbpr ?? null;
  const sbpReserveAmountFs = sbpReserveAmountAsPerFs ?? esopJson?.sbp_reserve?.amount_as_per_fs ?? null;
  const sbpReserveDifference =
    sbpReserveClosing != null && sbpReserveAmountFs != null
      ? Number(sbpReserveClosing) - Number(sbpReserveAmountFs)
      : null;
  const sbpReserveReconSum = sbpReserveReconRows.reduce((s, r) => s + (r.amount || 0), 0);
  const sbpReserveNetDifference =
    sbpReserveDifference != null ? sbpReserveDifference - sbpReserveReconSum : null;

  const addSbpReserveReconRow = () => {
    setSbpReserveReconRows([...sbpReserveReconRows, { particulars: "", amount: 0 }]);
  };
  const removeSbpReserveReconRow = (index: number) => {
    setSbpReserveReconRows(sbpReserveReconRows.filter((_, i) => i !== index));
  };
  const updateSbpReserveReconRow = (index: number, field: "particulars" | "amount", value: string | number) => {
    const updated = [...sbpReserveReconRows];
    if (field === "particulars") updated[index] = { ...updated[index], particulars: String(value) };
    else updated[index] = { ...updated[index], amount: Number(value) || 0 };
    setSbpReserveReconRows(updated);
  };

  const saveSbpReserve = async () => {
    setSbpReserveSaveStatus("saving");
    try {
      let current = esopJson;
      if (!current) {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(ESOP_JSON_FILE);
        if (result?.success && result?.data) current = result.data as ESOPJson;
      }
      current = current || ({} as ESOPJson);
      const closing = sbpReserveClosing != null ? Number(sbpReserveClosing) : null;
      const amountFs = sbpReserveAmountFs != null ? Number(sbpReserveAmountFs) : null;
      const diff = closing != null && amountFs != null ? closing - amountFs : null;
      const netDiff = diff != null ? diff - sbpReserveReconSum : null;
      const updatedSbpReserve: SbpReserve = {
        amount_as_per_fs: amountFs,
        amount_as_per_recomputation: current.sbp_reserve?.amount_as_per_recomputation ?? sbpReserveRecom ?? undefined,
        difference: diff,
        reconciliation_rows: sbpReserveReconRows.map((r) => ({ particulars: r.particulars, amount: r.amount })),
        net_difference: netDiff,
      };
      const updatedJson: ESOPJson = {
        ...current,
        sbp_reserve: updatedSbpReserve,
      };
      const jsonString = JSON.stringify(updatedJson, null, 4);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        setSbpReserveSaveStatus("error");
        return;
      }
      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: ESOP_JSON_FILE,
        referenceValue: "SBP Reserve",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_EBE_ESOP"],
      });
      if (result?.success) {
        setEsopJson(updatedJson);
        setSbpReserveSaveStatus("saved");
        setTimeout(() => setSbpReserveSaveStatus("idle"), 3000);
      } else {
        setSbpReserveSaveStatus("error");
      }
    } catch {
      setSbpReserveSaveStatus("error");
    }
  };

  const toggleCashPartyForLapse = (name: string) => {
    setCashSelectedPartyNamesForLapse((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const runCashLapseAnalysis = async () => {
    // Backend Python script will compute recomputation + reserve using selected party names
    if (!(window as any).payroll?.run) return;

    try {
      const lapseNames = cashSelectedPartyNamesForLapse && cashSelectedPartyNamesForLapse.length > 0
        ? cashSelectedPartyNamesForLapse
        : [];

      const result = await (window as any).payroll.run("execute_share_based_payment_lapse", {
        inputFiles: [],
        options: {
          lapse_names: lapseNames,
          json_name: ESOP_JSON_FILE,
        },
      });

      if (result?.ok) {
        // Python updates equity_sbp.recomputation, cash_sbp.recomputation, and sbp_reserve in Execution_EBE_ESOP.json
        await loadEsopJson();
      } else {
        setEsopLoadStatus("error");
      }
    } catch {
      setEsopLoadStatus("error");
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Share Based Payment</h2>
        <p className="text-sm text-white/60 mt-1">
          Employee Benefits Expense - Substantive Procedures
        </p>
      </div>

      {/* Ind AS 102 Assessment */}
      <Card className="border-white/10 bg-white/5">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <CardTitle className="text-white">Ind AS 102 Assessment</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {ind102SaveStatus === "saved" && (
                <span className="text-sm text-green-400">Saved</span>
              )}
              {ind102SaveStatus === "error" && ind102SaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                type="button"
                onClick={saveIndAs102Assessment}
                disabled={ind102Saving || isArchivalMode || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {ind102Saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Q1 */}
          <div className="space-y-3">
            <Label className="text-white font-medium">
              1. Is an agreement entered into by the company/ group company/
              shareholder of the group company with its employees (or any other
              party) which entitles the latter to:
            </Label>
            <ul className="list-disc list-inside text-white/80 text-sm space-y-1 ml-2">
              <li>Shares/ share options of the entity/ group entity OR</li>
              <li>
                Cash/ any other asset based on the share/ share option price of
                the entity/ group entity?
              </li>
            </ul>
            <RadioGroup value={q1} onValueChange={setQ1} className="flex gap-4 pt-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Yes" id="sbp-q1-yes" />
                <Label htmlFor="sbp-q1-yes" className="text-white cursor-pointer font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="No" id="sbp-q1-no" />
                <Label htmlFor="sbp-q1-no" className="text-white cursor-pointer font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>

          {showNoSbpFromQ1 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
              <p className="text-amber-200 text-sm">{NO_SBP_MESSAGE}</p>
            </div>
          )}

          {showQ2 && (
            <div className="space-y-3 pt-4 border-t border-white/10">
              <Label className="text-white font-medium">
                2. Does the same qualify to be considered as a share-based
                payment transaction where the entity:
              </Label>
              <ul className="list-disc list-inside text-white/80 text-sm space-y-1 ml-2">
                <li>
                  receives goods or services when another entity in the same
                  group (or a shareholder of any group entity) has the obligation
                  to settle the share-based payment transaction OR
                </li>
                <li>
                  has an obligation to settle a share-based payment transaction
                  when another entity in the same group receives the goods or
                  services, unless the transaction is clearly for a purpose
                  other than payment for goods or services supplied to the entity
                  receiving them.
                </li>
              </ul>
              <RadioGroup value={q2} onValueChange={setQ2} className="flex gap-4 pt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Yes" id="sbp-q2-yes" />
                  <Label htmlFor="sbp-q2-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="No" id="sbp-q2-no" />
                  <Label htmlFor="sbp-q2-no" className="text-white cursor-pointer font-normal">No</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {showNoSbpFromQ2 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
              <p className="text-amber-200 text-sm">{NO_SBP_MESSAGE}</p>
            </div>
          )}

          {showQ3ToQ6 && (
            <>
              <div className="space-y-3 pt-4 border-t border-white/10">
                <Label className="text-white font-medium">
                  3. Are the ESOP grants approved by appropriate authority in
                  accordance with Section 62(1)(b) of the Companies Act, 2013
                  read along with Rule 12 of Companies (Share Capital and
                  Debenture Rules, 2014)?
                </Label>
                <RadioGroup value={q3} onValueChange={setQ3} className="flex gap-4 pt-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="sbp-q3-yes" />
                    <Label htmlFor="sbp-q3-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="sbp-q3-no" />
                    <Label htmlFor="sbp-q3-no" className="text-white cursor-pointer font-normal">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3 pt-4 border-t border-white/10">
                <Label className="text-white font-medium">
                  4. Does the entity have equity-settled share-based payment (SBP)
                  transactions?
                </Label>
                <RadioGroup value={q4} onValueChange={setQ4} className="flex gap-4 pt-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="sbp-q4-yes" />
                    <Label htmlFor="sbp-q4-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="sbp-q4-no" />
                    <Label htmlFor="sbp-q4-no" className="text-white cursor-pointer font-normal">No</Label>
                  </div>
                </RadioGroup>
                {showQ4b && (
                  <div className="ml-4 mt-4 space-y-2 pl-4 border-l-2 border-white/20">
                    <Label className="text-white/90 font-medium">
                      Are there equity settled SBP for which the entity does not
                      have obligation to settle?
                    </Label>
                    <RadioGroup value={q4b} onValueChange={setQ4b} className="flex gap-4 pt-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Yes" id="sbp-q4b-yes" />
                        <Label htmlFor="sbp-q4b-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="No" id="sbp-q4b-no" />
                        <Label htmlFor="sbp-q4b-no" className="text-white cursor-pointer font-normal">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>

              <div className="space-y-3 pt-4 border-t border-white/10">
                <Label className="text-white font-medium">
                  5. Does the entity have cash-settled SBP transactions?
                </Label>
                <RadioGroup value={q5} onValueChange={setQ5} className="flex gap-4 pt-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="sbp-q5-yes" />
                    <Label htmlFor="sbp-q5-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="sbp-q5-no" />
                    <Label htmlFor="sbp-q5-no" className="text-white cursor-pointer font-normal">No</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3 pt-4 border-t border-white/10">
                <Label className="text-white font-medium">
                  6. Has the entity given any share based payments with a cash
                  alternative?
                </Label>
                <RadioGroup value={q6} onValueChange={setQ6} className="flex gap-4 pt-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="sbp-q6-yes" />
                    <Label htmlFor="sbp-q6-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="sbp-q6-no" />
                    <Label htmlFor="sbp-q6-no" className="text-white cursor-pointer font-normal">No</Label>
                  </div>
                </RadioGroup>
              </div>
            </>
          )}

          {ind102SaveError && ind102SaveStatus === "error" && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
              {ind102SaveError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SBP with Cash Alternative [when Q6 is Yes] – standalone card so it shows whenever Q6 is Yes */}
      {showQ3ToQ6 && q6 === "Yes" && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <CardTitle className="text-white">SBP with Cash Alternative</CardTitle>
            <p className="text-white/60 text-sm mt-1">Question 6 is Yes. Share based payments with a cash alternative.</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label className="text-white font-medium">
                1. Is the counterparty to which the payment plan is given any of the following?
              </Label>
              <RadioGroup value={cashAltCounterparty} onValueChange={setCashAltCounterparty} className="flex gap-6 pt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Employee" id="cash-alt-emp" />
                  <Label htmlFor="cash-alt-emp" className="text-white cursor-pointer font-normal">Employee</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Other than Employee" id="cash-alt-other" />
                  <Label htmlFor="cash-alt-other" className="text-white cursor-pointer font-normal">Other than Employee</Label>
                </div>
              </RadioGroup>
            </div>

            {cashAltCounterparty === "Employee" && (
              <div className="ml-4 space-y-4 pl-4 border-l-2 border-white/20">
                <div className="space-y-3">
                  <Label className="text-white font-medium">
                    1. Is the fair value of the cash alternative equal to that of the equity alternative?
                  </Label>
                  <RadioGroup value={cashAltFairValueEqual} onValueChange={setCashAltFairValueEqual} className="flex gap-4 pt-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="cash-alt-fv-yes" />
                      <Label htmlFor="cash-alt-fv-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="cash-alt-fv-no" />
                      <Label htmlFor="cash-alt-fv-no" className="text-white cursor-pointer font-normal">No</Label>
                    </div>
                  </RadioGroup>
                </div>
                {cashAltFairValueEqual === "Yes" && (
                  <div className="space-y-3">
                    <p className="text-white/90 text-sm bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                      Treat this as a cash settled SBP transaction and include in the cash settled SBP workings.
                    </p>
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm">Document</Label>
                      <Textarea
                        value={cashAltDocument}
                        onChange={(e) => setCashAltDocument(e.target.value)}
                        placeholder="Document your response here"
                        className="mt-1 border-white/10 bg-black/40 text-white min-h-[80px]"
                      />
                    </div>
                  </div>
                )}
                {cashAltFairValueEqual === "No" && (
                  <p className="text-white/90 text-sm bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                    The treatment of the excess of fair value of the option over the cash alternative has to be treated as equity settled SBP component while the cash alternative continues to be treated as cash settled SBP component.
                  </p>
                )}
              </div>
            )}

            {cashAltCounterparty === "Other than Employee" && (
              <p className="text-white/90 text-sm bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                The treatment of the grant should be as per compound financial instrument accounting as per Ind AS 109.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Equity-Settled Share Based Payment Transaction – assessment questions + Step 1 Upload, Step 2, Parameters, Run Analysis (immediately below Ind AS 102 when Q4 is Yes) */}
      {showEquitySection && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <CardTitle className="text-white">
              Equity-Settled Share Based Payment Transaction
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Assessment questions – before Step 1 */}
            <div className="space-y-3">
              <Label className="text-white font-medium">
                1. Are there any equity-settled SBP transactions with other than
                employees?
              </Label>
              <RadioGroup value={eq1} onValueChange={setEq1} className="flex gap-4 pt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Yes" id="eq1-yes" />
                  <Label htmlFor="eq1-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="No" id="eq1-no" />
                  <Label htmlFor="eq1-no" className="text-white cursor-pointer font-normal">No</Label>
                </div>
              </RadioGroup>
              {showEq1b && (
                <div className="ml-4 mt-4 space-y-2 pl-4 border-l-2 border-white/20">
                  <Label className="text-white/90 font-medium">
                    Have we considered the rebuttable presumption that the fair
                    value of the goods or services received can be measured
                    reliably?
                  </Label>
                  <RadioGroup value={eq1b} onValueChange={setEq1b} className="flex gap-4 pt-2">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Yes" id="eq1b-yes" />
                      <Label htmlFor="eq1b-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="No" id="eq1b-no" />
                      <Label htmlFor="eq1b-no" className="text-white cursor-pointer font-normal">No</Label>
                    </div>
                  </RadioGroup>
                  {showEq1Document && (
                    <div className="pt-2">
                      <Label className="text-white/80 text-sm">Document</Label>
                      <Textarea
                        value={eq1Document}
                        onChange={(e) => setEq1Document(e.target.value)}
                        placeholder="Document your response here"
                        className="mt-1 border-white/10 bg-black/40 text-white min-h-[80px]"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3 pt-4 border-t border-white/10">
              <Label className="text-white font-medium">
                2. For any equity-settled SBP transactions, have we considered
                the fair value of the share/ share options for the purpose of
                ESOP calculations?
              </Label>
              <RadioGroup value={eq2} onValueChange={setEq2} className="flex gap-4 pt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Yes" id="eq2-yes" />
                  <Label htmlFor="eq2-yes" className="text-white cursor-pointer font-normal">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="No" id="eq2-no" />
                  <Label htmlFor="eq2-no" className="text-white cursor-pointer font-normal">No</Label>
                </div>
              </RadioGroup>
              {showEq2Document && (
                <div className="pt-2">
                  <Label className="text-white/80 text-sm">Document</Label>
                  <Textarea
                    value={eq2Document}
                    onChange={(e) => setEq2Document(e.target.value)}
                    placeholder="Document your response here"
                    className="mt-1 border-white/10 bg-black/40 text-white min-h-[80px]"
                  />
                </div>
              )}
            </div>

            {/* Step 1: Upload ESOP Schedule (collapsible) */}
            <div className="pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => setEquityStep1Open((prev) => !prev)}
                className="w-full flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 text-left"
              >
                <span className="flex items-center gap-3 text-white font-medium">
                  <Search className="h-5 w-5 text-blue-500" />
                  Step 1: Upload ESOP Schedule
                </span>
                <ChevronDown className={`h-4 w-4 text-white/70 transition-transform ${equityStep1Open ? "rotate-180" : ""}`} />
              </button>
              {equityStep1Open && (
                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-3">
                    {fileSearchStatus === "searching" && (
                      <div className="flex items-center gap-2 text-blue-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Searching for files...</span>
                      </div>
                    )}
                    {fileSearchStatus === "found" && (
                      <span className="text-green-400 font-bold">FOUND!</span>
                    )}
                    {fileSearchStatus === "not_found" && (
                      <span className="text-yellow-400">No files found</span>
                    )}
                    {fileSearchStatus === "idle" && (
                      <span className="text-white/60">Searching for files...</span>
                    )}
                  </div>

                  {fileSearchStatus === "found" && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-white">Select File</Label>
                        <Select value={selectedFile} onValueChange={setSelectedFile}>
                          <SelectTrigger className="border-white/10 bg-black/40 text-white">
                            <SelectValue placeholder="Select file" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white">
                            {foundFiles.map((f) => (
                              <SelectItem key={f} value={f}>
                                {f}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {selectedFile && (
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={loadExcelColumns}
                            disabled={!selectedFile || isLoadingExcelColumns}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {isLoadingExcelColumns ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Loading Columns...
                              </>
                            ) : (
                              "Load Columns"
                            )}
                          </Button>
                          {excelColumns.length > 0 && (
                            <span className="text-green-400 text-sm">
                              {excelColumns.length} columns loaded
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {fileSearchStatus === "not_found" && (
                    <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
                      <div className="text-yellow-400 font-medium">
                        File not found. Please upload a file.
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-white">Reference Value (optional)</Label>
                          <Input
                            value={referenceValue}
                            onChange={(e) => setReferenceValue(e.target.value)}
                            placeholder="Enter reference value"
                            className="border-white/10 bg-black/40 text-white"
                          />
                        </div>
                        <Button
                          onClick={uploadEsopFile}
                          disabled={uploadStatus === "uploading"}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {uploadStatus === "uploading" ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload File
                            </>
                          )}
                        </Button>
                        {uploadStatus === "completed" && (
                          <span className="text-green-400">File uploaded successfully!</span>
                        )}
                        {uploadStatus === "error" && (
                          <span className="text-red-400">Upload failed</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Map Columns + Parameters + Run Analysis (collapsible) */}
            <div className="pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => setEquityStep2Open((prev) => !prev)}
                className="w-full flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 text-left"
              >
                <span className="text-white font-medium">Step 2: Map Columns, Parameters and Run Analysis</span>
                <ChevronDown className={`h-4 w-4 text-white/70 transition-transform ${equityStep2Open ? "rotate-180" : ""}`} />
              </button>
              {equityStep2Open && (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {ESOP_FIELD_KEYS.map(({ key: fieldKey, label: fieldLabel }) => (
                      <div key={fieldKey} className="space-y-2">
                        <Label className="text-white">{fieldLabel}</Label>
                        <Select
                          value={columnMappings[fieldKey] || ""}
                          onValueChange={(v) => handleMappingChange(fieldKey, v)}
                          disabled={excelColumns.length === 0}
                        >
                          <SelectTrigger className="border-white/10 bg-black/40 text-white">
                            <SelectValue
                              placeholder={
                                excelColumns.length === 0
                                  ? "Load columns first"
                                  : "Select column"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white">
                            {excelColumns.map((col) => (
                              <SelectItem key={col.value} value={col.value}>
                                {col.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4 pt-2 border-t border-white/10">
                    <h4 className="text-white font-medium">Parameters</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white">
                          Exercise period of ESOPs (if any)
                        </Label>
                        <div className="flex gap-2">
                          <Select
                            value={exercisePeriodUnit}
                            onValueChange={setExercisePeriodUnit}
                          >
                            <SelectTrigger className="w-[120px] border-white/10 bg-black/40 text-white">
                              <SelectValue placeholder="Unit" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-black/90 text-white">
                              <SelectItem value="Days">Days</SelectItem>
                              <SelectItem value="Months">Months</SelectItem>
                              <SelectItem value="Years">Years</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            value={exercisePeriodValue}
                            onChange={(e) => setExercisePeriodValue(e.target.value.replace(/\D/g, ""))}
                            placeholder="Enter period"
                            className="border-white/10 bg-black/40 text-white w-[100px]"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Reporting Date</Label>
                        <Input
                          type="date"
                          value={reportingDate}
                          onChange={(e) => setReportingDate(e.target.value)}
                          className="border-white/10 bg-black/40 text-white max-w-[200px]"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-white/10">
                    <Button
                      onClick={runAnalysis}
                      disabled={!selectedFile || !allEsopFieldsMapped}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Run Analysis
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cash-settled Share Based Payment Transactions [when Q5 is Yes]: same file section as Step 1: Upload ESOP Schedule, then columns, exercise period, Run Analysis */}
      {showCashSection && (
        <Card className="border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle className="text-white">Cash-settled Share Based Payment Transactions</CardTitle>
            <p className="text-white/60 text-sm mt-1">Question 5 is Yes. Upload ESOP Schedule and map columns.</p>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Step 1: Upload ESOP Schedule (collapsible) */}
            <div>
              <button
                type="button"
                onClick={() => setCashStep1Open((prev) => !prev)}
                className="w-full flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 text-left"
              >
                <span className="flex items-center gap-3 text-white font-medium">
                  <Search className="h-5 w-5 text-blue-500" />
                  Step 1: Upload ESOP Schedule
                </span>
                <ChevronDown className={`h-4 w-4 text-white/70 transition-transform ${cashStep1Open ? "rotate-180" : ""}`} />
              </button>
              {cashStep1Open && (
                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-3">
                    {cashFileSearchStatus === "searching" && (
                      <div className="flex items-center gap-2 text-blue-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Searching for files...</span>
                      </div>
                    )}
                    {cashFileSearchStatus === "found" && (
                      <span className="text-green-400 font-bold">FOUND!</span>
                    )}
                    {cashFileSearchStatus === "not_found" && (
                      <span className="text-yellow-400">No files found</span>
                    )}
                    {cashFileSearchStatus === "idle" && (
                      <span className="text-white/60">Searching for files...</span>
                    )}
                  </div>

                  {cashFileSearchStatus === "found" && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-white">Select File</Label>
                        <Select value={cashSelectedFile} onValueChange={setCashSelectedFile}>
                          <SelectTrigger className="border-white/10 bg-black/40 text-white">
                            <SelectValue placeholder="Select file" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white">
                            {cashFoundFiles.map((f) => (
                              <SelectItem key={f} value={f}>
                                {f}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {cashSelectedFile && (
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={loadCashExcelColumns}
                            disabled={!cashSelectedFile || cashIsLoadingExcelColumns}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {cashIsLoadingExcelColumns ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Loading Columns...
                              </>
                            ) : (
                              "Load Columns"
                            )}
                          </Button>
                          {cashExcelColumns.length > 0 && (
                            <span className="text-green-400 text-sm">
                              {cashExcelColumns.length} columns loaded
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {cashFileSearchStatus === "not_found" && (
                    <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
                      <div className="text-yellow-400 font-medium">
                        File not found. Please upload a file.
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-white">Reference Value (optional)</Label>
                          <Input
                            value={cashReferenceValue}
                            onChange={(e) => setCashReferenceValue(e.target.value)}
                            placeholder="Enter reference value"
                            className="border-white/10 bg-black/40 text-white"
                          />
                        </div>
                        <Button
                          onClick={uploadCashEsopFile}
                          disabled={cashUploadStatus === "uploading"}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {cashUploadStatus === "uploading" ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload File
                            </>
                          )}
                        </Button>
                        {cashUploadStatus === "completed" && (
                          <span className="text-green-400">File uploaded successfully!</span>
                        )}
                        {cashUploadStatus === "error" && (
                          <span className="text-red-400">Upload failed</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Map Columns + Parameters + Run Analysis (collapsible) */}
            <div className="pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => setCashStep2Open((prev) => !prev)}
                className="w-full flex items-center justify-between rounded-md border border-white/10 bg-white/5 px-3 py-2 text-left"
              >
                <span className="text-white font-medium">Step 2: Map Columns, Parameters and Run Analysis</span>
                <ChevronDown className={`h-4 w-4 text-white/70 transition-transform ${cashStep2Open ? "rotate-180" : ""}`} />
              </button>
              {cashStep2Open && (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {CASH_ESOP_FIELD_KEYS.map(({ key: fieldKey, label: fieldLabel }) => (
                      <div key={fieldKey} className="space-y-2">
                        <Label className="text-white">{fieldLabel}</Label>
                        <Select
                          value={cashColumnMappings[fieldKey] || ""}
                          onValueChange={(v) => handleCashMappingChange(fieldKey, v)}
                          disabled={cashExcelColumns.length === 0}
                        >
                          <SelectTrigger className="border-white/10 bg-black/40 text-white">
                            <SelectValue
                              placeholder={
                                cashExcelColumns.length === 0
                                  ? "Load columns first"
                                  : "Select column"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white">
                            {cashExcelColumns.map((col) => (
                              <SelectItem key={col.value} value={col.value}>
                                {col.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4 pt-2 border-t border-white/10">
                    <h4 className="text-white font-medium">Parameters</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white">Exercise period of ESOPs (if any)</Label>
                        <div className="flex gap-2">
                          <Select value={cashExercisePeriodUnit} onValueChange={setCashExercisePeriodUnit}>
                            <SelectTrigger className="w-[120px] border-white/10 bg-black/40 text-white">
                              <SelectValue placeholder="Unit" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-black/90 text-white">
                              <SelectItem value="Days">Days</SelectItem>
                              <SelectItem value="Months">Months</SelectItem>
                              <SelectItem value="Years">Years</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            value={cashExercisePeriodValue}
                            onChange={(e) => setCashExercisePeriodValue(e.target.value.replace(/\D/g, ""))}
                            placeholder="Enter period"
                            className="border-white/10 bg-black/40 text-white w-[100px]"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-white/10">
                    <Button
                      onClick={runCashAnalysis}
                      disabled={!cashSelectedFile || !allCashEsopFieldsMapped}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Run Analysis
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Equity group: ESOP table, Unvested lapsed, Reconciliation Summary – only when equity_sbp has data */}
      {hasEquitySbpData && (
      <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-4">
        <h3 className="text-white font-semibold text-lg">Equity-Settled</h3>
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              ESOP – Period To in current / previous financial year
            </CardTitle>
            <div className="flex items-center gap-2">
              {esopLoadStatus === "loaded" && (
                <span className="text-green-400 text-sm">Data loaded from {ESOP_JSON_FILE}</span>
              )}
              {esopLoadStatus === "error" && (
                <span className="text-amber-400 text-sm">No data or load failed</span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={loadEsopJson}
                disabled={esopLoadStatus === "loading"}
                className="border-white/20 text-white hover:bg-white/10"
              >
                {esopLoadStatus === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Refresh
              </Button>
            </div>
          </div>
          <p className="text-white/60 text-sm mt-1">
            Only rows where Period To falls in the financial year of the reporting date and the
            immediately preceding financial year (2-year range). Data from juggernaut folder (
            {ESOP_JSON_FILE}).
          </p>
        </CardHeader>
        <CardContent>
          {esopLoadStatus === "loading" && (
            <div className="flex items-center gap-2 text-white/70 py-8">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading table data...
            </div>
          )}
          {(esopLoadStatus === "idle" || esopLoadStatus === "error") && !filteredTableRows.length && (
            <p className="text-white/60 py-6">
              Click Refresh to load data from {ESOP_JSON_FILE} (if the file exists in SharePoint).
            </p>
          )}
          {esopLoadStatus === "loaded" && filteredTableRows.length === 0 && (
            <p className="text-white/60 py-6">
              No rows with Period To in the current/previous financial year.
            </p>
          )}
          {filteredTableRows.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-white/5">
                    <TableHead className="text-white/90 font-medium">Employee/ Party Name</TableHead>
                    <TableHead className="text-white/90 font-medium">Period From</TableHead>
                    <TableHead className="text-white/90 font-medium">Period To</TableHead>
                    <TableHead className="text-white/90 font-medium">Exercise Date</TableHead>
                    <TableHead className="text-white/90 font-medium">
                      Have the shares been allotted in CY?
                    </TableHead>
                    <TableHead className="text-white/90 font-medium">
                      Have the shares been allotted in PY?
                    </TableHead>
                    <TableHead className="text-white/90 font-medium">Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTableRows.map((row) => (
                    <TableRow key={getRowKey(row)} className="border-white/10 hover:bg-white/5">
                      <TableCell className="text-white">{row.employee_name}</TableCell>
                      <TableCell className="text-white">{row.period_from}</TableCell>
                      <TableCell className="text-white">{row.period_to}</TableCell>
                      <TableCell className="text-white">{row.exercise_date}</TableCell>
                      <TableCell>
                        <Select
                          value={getCyForRow(row)}
                          onValueChange={(v) => setCyForRow(row, v)}
                        >
                          <SelectTrigger className="w-[90px] border-white/10 bg-black/40 text-white">
                            <SelectValue placeholder="Yes/No" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white">
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={getPyForRow(row)}
                          onValueChange={(v) => setPyForRow(row, v)}
                          disabled={isPyDisabled(row)}
                        >
                          <SelectTrigger className="w-[90px] border-white/10 bg-black/40 text-white disabled:opacity-70">
                            <SelectValue placeholder="Yes/No" />
                          </SelectTrigger>
                          <SelectContent className="border-white/10 bg-black/90 text-white">
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-white">{getRemarksForRow(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {filteredTableRows.length > 0 && (
            <div className="flex items-center gap-3 pt-4 border-t border-white/10 mt-4">
              <Button
                onClick={saveEsopTable}
                disabled={esopSaveStatus === "saving"}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {esopSaveStatus === "saving" ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
              {esopSaveStatus === "saved" && (
                <span className="text-green-400 text-sm">Saved to {ESOP_JSON_FILE}</span>
              )}
              {esopSaveStatus === "error" && (
                <span className="text-red-400 text-sm">Save failed</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unvested options lapsed in CY + party selection + Run Analysis */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Unvested options lapsed</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-white font-medium">
              Have any unvested options lapsed in the current year?
            </Label>
            <RadioGroup
              value={unvestedLapsedCy}
              onValueChange={setUnvestedLapsedCy}
              className="flex gap-4 pt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Yes" id="unvested-yes" />
                <Label htmlFor="unvested-yes" className="text-white cursor-pointer font-normal">
                  Yes
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="No" id="unvested-no" />
                <Label htmlFor="unvested-no" className="text-white cursor-pointer font-normal">
                  No
                </Label>
              </div>
            </RadioGroup>
          </div>
          {unvestedLapsedCy === "Yes" && (
            <div className="space-y-2">
              <Label className="text-white">Select party names (for computation: total expense booked for those rows)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-white/10"
                  >
                    <span className="truncate">
                      {selectedPartyNamesForLapse.length === 0
                        ? "Choose party names..."
                        : `${selectedPartyNamesForLapse.length} selected`}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] border-white/10 bg-black/90 text-white p-2 max-h-[280px] overflow-y-auto">
                  <div className="space-y-2">
                    {allPartyNames.map((name) => (
                      <div
                        key={name}
                        className="flex items-center space-x-2 rounded px-2 py-1.5 hover:bg-white/10"
                      >
                        <Checkbox
                          id={`lapse-${name}`}
                          checked={selectedPartyNamesForLapse.includes(name)}
                          onCheckedChange={() => togglePartyForLapse(name)}
                          className="border-white/30 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                        />
                        <label
                          htmlFor={`lapse-${name}`}
                          className="text-sm cursor-pointer flex-1 select-none"
                        >
                          {name}
                        </label>
                      </div>
                    ))}
                    {allPartyNames.length === 0 && (
                      <p className="text-white/60 text-sm py-2">Load ESOP data to see party names.</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {selectedPartyNamesForLapse.length > 0 && (
                <p className="text-white/60 text-xs">
                  Selected: {selectedPartyNamesForLapse.join(", ")}
                </p>
              )}
            </div>
          )}
          <div className="pt-2">
            <Button
              onClick={runLapseAnalysis}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Run Analysis
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ESOP Reconciliation Summary – Particulars / Amount (dummy data, Actuary-style) */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">ESOP Reconciliation Summary</CardTitle>
          <p className="text-white/60 text-sm mt-1">
            Expense as per P&amp;L vs recomputation. Dummy data for now; download/display logic to be implemented later.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-white/10">
              <thead>
                <tr className="bg-black/60">
                  <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                  <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold w-40">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">
                    <div className="space-y-1">
                      <span>Expense as per Profit &amp; Loss (D)</span>
                      <div className="mt-1">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between border-white/10 bg-black/40 text-white text-sm hover:bg-white/10"
                            >
                              <span className="truncate">
                                {equitySelectedExpenseLedgers.length === 0
                                  ? "Choose ledgers..."
                                  : `${equitySelectedExpenseLedgers.length} ledgers selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[360px] border-white/10 bg-black/90 text-white p-2 max-h-[280px] overflow-y-auto">
                            <div className="space-y-1 text-xs text-white/60 mb-1">
                              <div>Select ledger accounts comprising of ESOP expense.</div>
                              <div>Sum of chosen = Amount.</div>
                            </div>
                            <div className="space-y-1">
                              {equityExpenseLedgerOptions.map((opt) => {
                                const checked = equitySelectedExpenseLedgers.includes(opt.name);
                                return (
                                  <div
                                    key={opt.name}
                                    className="flex items-center justify-between space-x-2 rounded px-2 py-1.5 hover:bg-white/10"
                                  >
                                    <label className="flex-1 text-xs cursor-pointer select-none">
                                      {opt.name}
                                    </label>
                                    <span className="text-xs text-white/70">
                                      {opt.amount.toLocaleString()}
                                    </span>
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={() =>
                                        setEquitySelectedExpenseLedgers((prev) =>
                                          checked
                                            ? prev.filter((n) => n !== opt.name)
                                            : [...prev, opt.name]
                                        )
                                      }
                                      className="ml-2 border-white/30 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                                    />
                                  </div>
                                );
                              })}
                              {equityExpenseLedgerOptions.length === 0 && (
                                <p className="text-white/60 text-xs py-2">
                                  No Expense-type ledgers found in trial balance mapping.
                                </p>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <span className="text-white/50 text-xs mt-1 block">
                          Sum of chosen = Amount
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {expenseAsPerPLSum.toLocaleString()}
                  </td>
                </tr>
                <tr className="hover:bg-white/5 bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-medium" colSpan={2}>
                    Expense as per Recomputation
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8">
                    Expense as per ESOP Working (A)
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {expenseAsPerESOPWorking.toLocaleString()}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8">
                    Less: expense booked till date on unvested options lapsed (B)
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    ({expenseBookedUnvestedLapsed.toLocaleString()})
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8 font-medium">
                    Net expense to Profit &amp; Loss (C) = A − B
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-medium">
                    {netExpenseToPL.toLocaleString()}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-medium">
                    Difference (C − D)
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-medium">
                    {differenceCD.toLocaleString()}
                  </td>
                </tr>
                {reconUserRows.map((row, index) => (
                  <tr key={`recon-user-${index}`} className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">{row.reason || `User row ${index + 1}`}</td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white">{row.amount || 0}</td>
                  </tr>
                ))}
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">
                    Net Difference
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {netDifference.toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* User rows to reconcile difference (Actuary-style) */}
          <div className="space-y-4 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <Label className="text-white text-lg font-semibold">User rows to reconcile difference</Label>
              <Button
                onClick={addReconUserRow}
                size="sm"
                variant="outline"
                className="border-white/10 text-white hover:bg-white/10"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Row
              </Button>
            </div>
            {reconUserRows.length === 0 ? (
              <p className="text-sm text-white/50">No rows added. Click &quot;Add Row&quot; to add one.</p>
            ) : (
              <div className="space-y-3">
                {reconUserRows.map((row, index) => (
                  <div
                    key={index}
                    className="flex gap-3 items-start p-3 border border-white/10 rounded-lg bg-black/20"
                  >
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-white text-sm">Particulars</Label>
                        <Input
                          type="text"
                          value={row.reason}
                          onChange={(e) => updateReconUserRow(index, "reason", e.target.value)}
                          className="border-white/10 bg-black/40 text-white"
                          placeholder="Enter particulars"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-white text-sm">Amount</Label>
                        <Input
                          type="number"
                          value={row.amount || ""}
                          onChange={(e) => updateReconUserRow(index, "amount", e.target.value)}
                          className="border-white/10 bg-black/40 text-white"
                          placeholder="Amount"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={() => removeReconUserRow(index)}
                      size="sm"
                      variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20 mt-6"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-white/10">
            <Button
              onClick={saveEquityRecomputation}
              disabled={esopSaveStatus === "saving"}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {esopSaveStatus === "saving" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
            {esopSaveStatus === "saved" && (
              <span className="text-green-400 text-sm">Reconciliation saved to {ESOP_JSON_FILE}</span>
            )}
            {esopSaveStatus === "error" && (
              <span className="text-red-400 text-sm">Save failed</span>
            )}
          </div>
        </CardContent>
      </Card>
      </div>
      )}

      {/* Cash group: table + unvested + recon – when cash_sbp has data */}
      {hasCashSbpData && (
      <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-4">
        <h3 className="text-white font-semibold text-lg">Cash-Settled</h3>
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              Cash-settled Share Based Payment Transactions – Period To in current / previous financial year
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-green-400 text-sm">Data from cash_sbp in {ESOP_JSON_FILE}</span>
              <Button variant="outline" size="sm" onClick={loadEsopJson} disabled={esopLoadStatus === "loading"} className="border-white/20 text-white hover:bg-white/10">
                {esopLoadStatus === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Refresh
              </Button>
            </div>
          </div>
          <p className="text-white/60 text-sm mt-1">
            Only rows where Period To falls in the financial year of the reporting date and the immediately preceding financial year. Data from cash_sbp.
          </p>
        </CardHeader>
        <CardContent>
          {filteredCashTableRows.length === 0 ? (
            <p className="text-white/60 py-6">No rows with Period To in the current/previous financial year.</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-white/5">
                      <TableHead className="text-white/90 font-medium">Employee/ Party Name</TableHead>
                      <TableHead className="text-white/90 font-medium">Period From</TableHead>
                      <TableHead className="text-white/90 font-medium">Period To</TableHead>
                      <TableHead className="text-white/90 font-medium">Exercise Date</TableHead>
                      <TableHead className="text-white/90 font-medium">Have the shares been allotted in CY?</TableHead>
                      <TableHead className="text-white/90 font-medium">Have the shares been allotted in PY? (PY = year before reporting period)</TableHead>
                      <TableHead className="text-white/90 font-medium">Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCashTableRows.map((row) => (
                      <TableRow key={getRowKey(row)} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white">{row.employee_name}</TableCell>
                        <TableCell className="text-white">{row.period_from}</TableCell>
                        <TableCell className="text-white">{row.period_to}</TableCell>
                        <TableCell className="text-white">{row.exercise_date}</TableCell>
                        <TableCell>
                          <Select value={getCashCyForRow(row)} onValueChange={(v) => setCashCyForRow(row, v)}>
                            <SelectTrigger className="w-[90px] border-white/10 bg-black/40 text-white">
                              <SelectValue placeholder="Yes/No" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-black/90 text-white">
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select value={getCashPyForRow(row)} onValueChange={(v) => setCashPyForRow(row, v)} disabled={isCashPyDisabled(row)}>
                            <SelectTrigger className="w-[90px] border-white/10 bg-black/40 text-white disabled:opacity-70">
                              <SelectValue placeholder="Yes/No" />
                            </SelectTrigger>
                            <SelectContent className="border-white/10 bg-black/90 text-white">
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-white">{getCashRemarksForRow(row)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10 mt-4">
                <Button onClick={saveCashEsopTable} disabled={cashEsopSaveStatus === "saving"} className="bg-green-600 hover:bg-green-700 text-white">
                  {cashEsopSaveStatus === "saving" ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : <><Save className="h-4 w-4 mr-2" /> Save</>}
                </Button>
                {cashEsopSaveStatus === "saved" && <span className="text-green-400 text-sm">Saved to {ESOP_JSON_FILE}</span>}
                {cashEsopSaveStatus === "error" && <span className="text-red-400 text-sm">Save failed</span>}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Cash-settled – Unvested options lapsed</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-white font-medium">Have any unvested options lapsed in the current year?</Label>
            <RadioGroup value={cashUnvestedLapsedCy} onValueChange={setCashUnvestedLapsedCy} className="flex gap-4 pt-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Yes" id="cash-unvested-yes" />
                <Label htmlFor="cash-unvested-yes" className="text-white cursor-pointer font-normal">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="No" id="cash-unvested-no" />
                <Label htmlFor="cash-unvested-no" className="text-white cursor-pointer font-normal">No</Label>
              </div>
            </RadioGroup>
          </div>
          {cashUnvestedLapsedCy === "Yes" && (
            <div className="space-y-2">
              <Label className="text-white">Select party names</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between border-white/10 bg-black/40 text-white hover:bg-white/10">
                    <span className="truncate">
                      {cashSelectedPartyNamesForLapse.length === 0 ? "Choose party names..." : `${cashSelectedPartyNamesForLapse.length} selected`}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] border-white/10 bg-black/90 text-white p-2 max-h-[280px] overflow-y-auto">
                  <div className="space-y-2">
                    {cashAllPartyNames.map((name) => (
                      <div key={name} className="flex items-center space-x-2 rounded px-2 py-1.5 hover:bg-white/10">
                        <Checkbox id={`cash-lapse-${name}`} checked={cashSelectedPartyNamesForLapse.includes(name)} onCheckedChange={() => toggleCashPartyForLapse(name)} className="border-white/30 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                        <label htmlFor={`cash-lapse-${name}`} className="text-sm cursor-pointer flex-1 select-none">{name}</label>
                      </div>
                    ))}
                    {cashAllPartyNames.length === 0 && <p className="text-white/60 text-sm py-2">No party names in cash_sbp data.</p>}
                  </div>
                </PopoverContent>
              </Popover>
              {cashSelectedPartyNamesForLapse.length > 0 && <p className="text-white/60 text-xs">Selected: {cashSelectedPartyNamesForLapse.join(", ")}</p>}
            </div>
          )}
          <div className="pt-2">
            <Button onClick={runCashLapseAnalysis} className="bg-green-600 hover:bg-green-700 text-white">Run Analysis</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Cash-settled – ESOP Reconciliation Summary</CardTitle>
          <p className="text-white/60 text-sm mt-1">
            Expense as per P&amp;L vs recomputation, loaded and saved from the cash_sbp.recomputation section in {ESOP_JSON_FILE}.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-white/10">
              <thead>
                <tr className="bg-black/60">
                  <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                  <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold w-40">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">
                    <div className="space-y-1">
                      <span>Expense as per Profit &amp; Loss (D)</span>
                      <div className="mt-1">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between border-white/10 bg-black/40 text-white text-sm hover:bg-white/10"
                            >
                              <span className="truncate">
                                {cashSelectedExpenseLedgers.length === 0
                                  ? "Choose ledgers..."
                                  : `${cashSelectedExpenseLedgers.length} ledgers selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[360px] border-white/10 bg-black/90 text-white p-2 max-h-[280px] overflow-y-auto">
                            <div className="space-y-1 text-xs text-white/60 mb-1">
                              <div>Select ledger accounts comprising of ESOP cash expense.</div>
                              <div>Sum of chosen = Amount.</div>
                            </div>
                            <div className="space-y-1">
                              {equityExpenseLedgerOptions.map((opt) => {
                                const checked = cashSelectedExpenseLedgers.includes(opt.name);
                                return (
                                  <div
                                    key={opt.name}
                                    className="flex items-center justify-between space-x-2 rounded px-2 py-1.5 hover:bg-white/10"
                                  >
                                    <label className="flex-1 text-xs cursor-pointer select-none">
                                      {opt.name}
                                    </label>
                                    <span className="text-xs text-white/70">
                                      {opt.amount.toLocaleString()}
                                    </span>
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={() =>
                                        setCashSelectedExpenseLedgers((prev) =>
                                          checked
                                            ? prev.filter((n) => n !== opt.name)
                                            : [...prev, opt.name]
                                        )
                                      }
                                      className="ml-2 border-white/30 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                                    />
                                  </div>
                                );
                              })}
                              {equityExpenseLedgerOptions.length === 0 && (
                                <p className="text-white/60 text-xs py-2">
                                  No Expense-type ledgers found in trial balance mapping.
                                </p>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <span className="text-white/50 text-xs mt-1 block">
                          Sum of chosen = Amount
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {cashExpenseAsPerPLSum.toLocaleString()}
                  </td>
                </tr>
                <tr className="hover:bg-white/5 bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-medium" colSpan={2}>Expense as per Recomputation</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8">Expense as per ESOP Working (A)</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {cashExpenseAsPerESOPWorking.toLocaleString()}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8">Less: expense booked till date on unvested options lapsed (B)</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    ({cashExpenseBookedUnvestedLapsed.toLocaleString()})
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8 font-medium">Net expense to Profit &amp; Loss (C) = A − B</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-medium">
                    {cashNetExpenseToPL.toLocaleString()}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-medium">Difference (C − D)</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-medium">{cashDifferenceCD.toLocaleString()}</td>
                </tr>
                {cashReconUserRows.map((row, index) => (
                  <tr key={`cash-recon-${index}`} className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">{row.reason || `User row ${index + 1}`}</td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white">{row.amount || 0}</td>
                  </tr>
                ))}
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">{cashNetDifference.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="space-y-4 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <Label className="text-white text-lg font-semibold">User rows to reconcile difference</Label>
              <Button onClick={addCashReconUserRow} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/10">
                <Plus className="h-4 w-4 mr-2" /> Add Row
              </Button>
            </div>
            {cashReconUserRows.length === 0 ? (
              <p className="text-sm text-white/50">No rows added. Click &quot;Add Row&quot; to add one.</p>
            ) : (
              <div className="space-y-3">
                {cashReconUserRows.map((row, index) => (
                  <div key={index} className="flex gap-3 items-start p-3 border border-white/10 rounded-lg bg-black/20">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-white text-sm">Particulars</Label>
                        <Input type="text" value={row.reason} onChange={(e) => updateCashReconUserRow(index, "reason", e.target.value)} className="border-white/10 bg-black/40 text-white" placeholder="Enter particulars" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-white text-sm">Amount</Label>
                        <Input type="number" value={row.amount || ""} onChange={(e) => updateCashReconUserRow(index, "amount", e.target.value)} className="border-white/10 bg-black/40 text-white" placeholder="Amount" />
                      </div>
                    </div>
                    <Button onClick={() => removeCashReconUserRow(index)} size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/20 mt-6">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-white/10">
            <Button
              onClick={saveCashRecomputation}
              disabled={cashEsopSaveStatus === "saving"}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {cashEsopSaveStatus === "saving" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
            {cashEsopSaveStatus === "saved" && (
              <span className="text-green-400 text-sm">Reconciliation saved to {ESOP_JSON_FILE}</span>
            )}
            {cashEsopSaveStatus === "error" && (
              <span className="text-red-400 text-sm">Save failed</span>
            )}
          </div>
        </CardContent>
      </Card>
      </div>
      )}

      {/* SBP Reserve – always visible at bottom; data from Execution_EBE_ESOP.json sbp_reserve */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">SBP Reserve</CardTitle>
          <p className="text-white/60 text-sm mt-1">
            Amount as per FS from note line {NOTE_LINE_SBP_RESERVE}. Recomputation and reconciliation from {ESOP_JSON_FILE}.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-white/10">
              <thead>
                <tr className="bg-black/60">
                  <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                  <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold w-48">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">
                    <div className="flex items-center gap-2">
                      <span>Amount as per FS</span>
                      <span className="text-white/50 text-xs">Total of note line ID {NOTE_LINE_SBP_RESERVE}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={loadNoteLine30105Total}
                        disabled={sbpReserveLoadFsStatus === "loading"}
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        {sbpReserveLoadFsStatus === "loading" ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Load from TB"
                        )}
                      </Button>
                    </div>
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {sbpReserveAmountFs != null ? Number(sbpReserveAmountFs).toLocaleString() : "—"}
                  </td>
                </tr>
                <tr className="bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-medium" colSpan={2}>
                    Amount as per Recomputation
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8">1 Opening Balance</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {sbpReserveRecom?.opening_balance != null
                      ? Number(sbpReserveRecom.opening_balance).toLocaleString()
                      : "—"}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8">2 Add: Expense</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {sbpReserveRecom?.add_expense != null
                      ? Number(sbpReserveRecom.add_expense).toLocaleString()
                      : "—"}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8">3 Less: Vested and allotted</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {sbpReserveRecom?.less_vested_and_allotted != null
                      ? Number(sbpReserveRecom.less_vested_and_allotted).toLocaleString()
                      : "—"}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8">4 Less: Vested and lapsed</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {sbpReserveRecom?.less_vested_and_lapsed != null
                      ? Number(sbpReserveRecom.less_vested_and_lapsed).toLocaleString()
                      : "—"}
                  </td>
                </tr>
                <tr className="hover:bg-white/5 bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white pl-8 font-medium">Closing SBPR</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {sbpReserveClosing != null ? Number(sbpReserveClosing).toLocaleString() : "—"}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-medium">Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {sbpReserveDifference != null ? sbpReserveDifference.toLocaleString() : "—"}
                  </td>
                </tr>
                <tr className="bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-medium">User to reconcile</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">—</td>
                </tr>
                {sbpReserveReconRows.map((row, index) => (
                  <tr key={index} className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2">
                      <Input
                        value={row.particulars}
                        onChange={(e) => updateSbpReserveReconRow(index, "particulars", e.target.value)}
                        className="border-white/10 bg-black/40 text-white"
                        placeholder="Particulars"
                      />
                    </td>
                    <td className="border border-white/10 px-4 py-2">
                      <Input
                        type="number"
                        value={row.amount || ""}
                        onChange={(e) => updateSbpReserveReconRow(index, "amount", e.target.value)}
                        className="border-white/10 bg-black/40 text-white text-right"
                        placeholder="Amount"
                      />
                    </td>
                  </tr>
                ))}
                <tr className="hover:bg-white/5 bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-medium">Net Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white">
                    {sbpReserveNetDifference != null ? sbpReserveNetDifference.toLocaleString() : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 pt-4 border-t border-white/10">
            <Button type="button" variant="outline" size="sm" onClick={addSbpReserveReconRow} className="border-white/20 text-white hover:bg-white/10">
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
            <Button
              onClick={saveSbpReserve}
              disabled={sbpReserveSaveStatus === "saving"}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {sbpReserveSaveStatus === "saving" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
            {sbpReserveSaveStatus === "saved" && (
              <span className="text-green-400 text-sm">Saved to {ESOP_JSON_FILE}</span>
            )}
            {sbpReserveSaveStatus === "error" && (
              <span className="text-red-400 text-sm">Save failed</span>
            )}
          </div>
          <p className="text-white/50 text-xs">
            Load {ESOP_JSON_FILE} to populate recomputation. Save updates sbp_reserve and uploads to juggernaut.
          </p>
        </CardContent>
      </Card>

      {/* Inference and Conclusion */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">{IND_AS_102_DOC_QUESTION}</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === "saved" && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === "error" && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                type="button"
                onClick={saveInferenceQuestion}
                disabled={inferenceSaving || isArchivalMode || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DocumentManager
            sectionKey={IND_AS_102_SECTION_KEY}
            groupKey="custom-procedure"
            itemId={IND_AS_102_INFERENCE_QUESTION_ID}
            itemQuestion={IND_AS_102_DOC_QUESTION}
            itemTextResponse={inferenceQuestionResponse.textResponse ?? null}
            itemDocuments={inferenceQuestionResponse.documents || []}
            onDocumentsChange={(documents) =>
              setInferenceQuestionResponse((prev) => ({ ...prev, documents }))
            }
            onTextResponseChange={(textResponse) =>
              setInferenceQuestionResponse((prev) => ({ ...prev, textResponse }))
            }
            disabled={isArchivalMode}
          />
          {inferenceSaveError && inferenceSaveStatus === "error" && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
              {inferenceSaveError}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
